import { SetStats, calculate1RM, estimate1RM, estimateWeightForReps } from '@cf-shared/data-model/set-stats';
import { adjustWeight, Equipment, EquipmentType, isEquipmentValid, MANUAL_EQUIPMENT_MAX_POSITIONS, WEIGHT_GRID_TOLERANCE_KG } from '@cf-shared/data-model/equipment';
import { getMinTargetRepsForIntensity, IntensityLevel, Plan_ExercisePath } from '@cf-shared/data-model/fitness-plan';
import { PostSetStatus, SetSuccess, SetType } from '@cf-shared/enums';
import { Log_RetestDemandBackup, Log_WorkoutExercise, Log_WorkoutSet } from '@cf-shared/data-model/log-workout';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { defaultCustomWeightIncrement_kg, isPlausibleEquipmentStep_kg } from 'util/custom-weight-increment';
import { deepClone } from '@cf-shared/deep-helpers';
import { SessionRecency } from 'util/historical-subslot';

/**
 * True when the logged weight is a real loadable step below the recommended (already grid-snapped)
 * weight. The logged weight is snapped to the equipment's loadable grid first, so a pounds-configured
 * barbell that stores the same nominal weight (e.g. 170 lb) as a hair-different kilogram value — pure
 * lb→kg float noise — does NOT read as "lower than recommended" (#3824). A genuinely lighter load (at
 * least one loadable increment down, including one microplate) still reads as lower. This mirrors how
 * the rest of the workout flow reasons about weights on the equipment grid via `adjustWeight('round')`.
 */
export function isLoggedWeightBelowRecommended(loggedWeight_kg: number, recommendedWeight_kg: number, equipment: Equipment): boolean {
  // snap the logged weight onto the equipment's loadable grid so two representations of the same
  // nominal weight (e.g. 170 lb stored as a hair-different kg) collapse to the identical value; only
  // then is a strictly-lower result a real lighter load rather than lb→kg float noise
  const snappedLogged_kg = adjustWeight(loggedWeight_kg, 'round', equipment).weight_kg;
  return snappedLogged_kg < recommendedWeight_kg;
}

/**
 * Which weight the "you used a weight lower than the recommended weight of X" message should name.
 *
 * Two things must hold at once. The named weight must match what the rest of the screen shows, which
 * is `logSet.plannedWeight_kg` — the set row and the bracketed weight both render it. And it must be
 * above the weight the user logged, or the sentence contradicts itself.
 *
 * They can disagree, because `plannedWeight_kg` is snapped when the set is BUILT while the decision
 * to show the message uses the target snapped onto the equipment attached at conclusion. Change the
 * equipment in between and the snapshot can sit below the logged weight. When it does, the freshly
 * snapped target is the only value that can honestly be named.
 *
 * The COMPARISON that decides whether to show the message at all is deliberately not touched here —
 * it must stay on the equipment's loadable grid, which is what #3824 fixed.
 *
 * Why this is not simply `roundedTargetWeight_kg`, and the report behind it (#5325):
 * `design-docs/reactive-deload.md`, the #4337 section.
 */
export function advisoryRecommendedWeight_kg(
  plannedWeight_kg: number | null | undefined,
  roundedTargetWeight_kg: number,
  loggedWeight_kg: number,
): number {
  const plannedIsUsable = typeof plannedWeight_kg === 'number' && isFinite(plannedWeight_kg) && plannedWeight_kg > loggedWeight_kg;
  return plannedIsUsable ? plannedWeight_kg : roundedTargetWeight_kg;
}

/**
 * The step size inferred for the temporary "automatic" equipment created when a set is accepted at a
 * weight the assigned equipment cannot produce (an "unbrowse" — see WorkoutDetailPage.onClickSetDone).
 * Normally this is the plain difference between this session's weight and the last logged session's
 * weight. But when the two sessions used the exact same weight (#4337 — a real report: the user's
 * target weight was not a step their equipment supports, and they lifted the same weight twice in a
 * row), that difference is 0 — not a usable equipment step. `isEquipmentValid` requires a strictly
 * positive `weight_inc_kg`, so a 0 (or NaN) increment silently makes the synthetic equipment invalid,
 * which in turn makes `adjustWeight` return the weight unchanged and falsely trips the "maxed out
 * equipment" check on the very next set. This falls back to the same default step used when there is
 * no previous session to compare against at all, since a same-weight repeat carries the same "no
 * usable delta" information.
 *
 * Three further rules reject a guess that is positive but still not an equipment step: an oversized
 * difference (#5344), a step that cannot load the weight it was guessed from (#5325), and a step
 * that is round in neither unit (#5344). All three fall back to the same shared default. The reports
 * behind them, with the production numbers: `design-docs/reactive-deload.md`, the #4337 section.
 */
export function computeUnbrowsedCustomWeightIncrement_kg(
  currentWeight_kg: number,
  lastWeight_kg: number,
  incrementUnit: 'kg' | 'lb',
): number {
  const rawIncrement = Math.abs(currentWeight_kg - lastWeight_kg);
  // Every rejection below lands on the same shared default — the step a user with no previous
  // session to compare against gets — so they are one guarded assignment rather than four.
  //
  // - a difference bigger than half the current weight (#5344). This used to be replaced by the
  //   current weight itself, so that a single step down landed exactly on 0; what it produced was a
  //   grid of 0, 80, 160 for somebody training at 80 kg, which is not a machine anybody owns.
  // - not a usable positive step at all: 0, negative or NaN — the #4337 same-weight case, or a
  //   missing weight upstream.
  // - a step that cannot load the very weight it was inferred from (#5325), so the invented
  //   equipment could not produce the weight it was built for.
  // - a step round in neither kilograms nor pounds (#5344): the leftover of two weights that were
  //   never a step apart. Same predicate the boot migration and the read-time heal use, so the
  //   write path cannot keep re-creating what they repair.
  const isOversized = currentWeight_kg - rawIncrement < rawIncrement;
  if (isOversized ||
      !canEquipmentStepLoadWeight(rawIncrement, currentWeight_kg) ||
      !isPlausibleEquipmentStep_kg(rawIncrement)) {
    return defaultCustomWeightIncrement_kg(incrementUnit);
  }
  return rawIncrement;
}

/**
 * Can a synthesized "automatic" equipment stepping in `step_kg` actually load `weight_kg`?
 *
 * The equipment `UiExercise.checkEquipment` builds from an inferred step starts at 0 kg and goes up
 * one step at a time, so the only weights it can load are whole multiples of that step, and only as
 * far up as the app enumerates positions. A step that fails this test is not a description of
 * anybody's gym: it is the leftover of two weights that were never a step apart.
 *
 * Both bounds are the app's existing rules rather than new thresholds, which is what keeps a genuine
 * microplate step: `WEIGHT_GRID_TOLERANCE_KG` is the slack `adjustWeight` already allows before it
 * reports a weight as off-grid, and `MANUAL_EQUIPMENT_MAX_POSITIONS` is what `getPossibleWeights`
 * enumerates. Deliberately NOT a minimum step size — no magnitude floor can separate the leftover
 * this rejects from a real 0.5 lb microplate.
 *
 * The report behind it (#5325), with the numbers: `design-docs/reactive-deload.md`, the #4337 section.
 */
function canEquipmentStepLoadWeight(step_kg: number, weight_kg: number): boolean {
  const positions = Math.round(weight_kg / step_kg);
  if (!(positions >= 0 && positions <= MANUAL_EQUIPMENT_MAX_POSITIONS)) {
    return false;
  }
  return Math.abs(weight_kg - positions * step_kg) < WEIGHT_GRID_TOLERANCE_KG;
}

/**
 * Pure decision logic for what happens after the user logs the FIRST (benchmark) set of an exercise
 * on a normal training day (`SetType.Regular1st`). Extracted out of `WorkoutDetailPage.concludeSet`
 * so the branching can be unit-tested in isolation (the component method interleaves this decision
 * with side effects — saving the plan, showing alerts, telemetry — that make it impractical to test
 * end-to-end deterministically; the workout loader normalizes seeded plan state, see #3720).
 *
 * The decision is a pure function of: the logged set's stats, the prescribed target weight/reps, the
 * armed `min_reps` floor, and the two historical baselines the engine keeps (`previousFirstSetStats`
 * — the previous workout's strongest successful set — and `planPath.lastStats`). It returns one of three
 * outcomes; the component performs the matching side effects.
 */
export enum Regular1stOutcome {
  /** Hit the prescribed weight and reps → linear progression. */
  TargetHit = 'targetHit',
  /** Cleared the floor but not full target reps → keep weight, bump reps next time. */
  MinReps = 'minReps',
  /** No sign of progress → offer a reactive deload. */
  Deload = 'deload',
}

/**
 * The benchmark weight this session's first-set evaluation judges the logged set against — it feeds
 * the whole `Regular1st` evaluation, not only the deload decision (also the rep-correctness check and
 * the "lower than recommended weight" hint). Normally this is the path's climbing `target_weight_kg`.
 * But in the older layoff "rep-fallback" mode (#3924, sibling of #3339) the app HELD the weight at the
 * last lifted weight and only cut the rep target — so the higher climbing target would judge the user
 * against a weight they were never asked to lift, wrongly offering a deload even when they hit exactly
 * what was prescribed. In that mode, measure against the held weight. Since #5617 a layoff worked out
 * from the best recent set stores the asked-for weight AS the target, so its sets fall through to the
 * target; the held-weight reading stays for sets logged before that and for the older rule's fallback.
 *
 * The prescription cap (#5176) is the same shape of problem and takes precedence over both: when the
 * loader lowered the weight because the plan asked for more than the user can perform, the lowered
 * weight IS what the user was shown and prefilled with, so it is the only honest thing to judge them
 * against. It wins outright rather than being combined, because the loader applies it AFTER the
 * layoff block and only when it lands below whatever that block left — so the capped weight is
 * already the lower of the two.
 */
export function benchmarkTargetWeight_kg(args: BenchmarkWeightInput): number | undefined {
  if (isBenchmarkCapped(args)) {
    return args.cappedWeight_kg;
  }
  if (isBenchmarkLayoffHeld(args)) {
    return args.heldWeight_kg;
  }
  return args.targetWeight_kg;
}

export interface BenchmarkWeightInput {
  /** The path's climbing target weight. */
  targetWeight_kg: number | undefined;
  /** The last lifted weight the rep-fallback layoff held the session at (`lastStats.weight_kg`). */
  heldWeight_kg: number | undefined;
  /** True when the rep-fallback layoff branch fired this session (weight held, rep target cut). */
  layoffHeldWeightWithReducedReps: boolean;
  /** The weight the prescription cap lowered this session to. Pick it with `cappedBenchmarkWeight_kg`:
   *  a follow-up set's own `plannedWeight_kg` is the previous set's logged weight, not the cap. */
  cappedWeight_kg?: number | undefined;
  /** True when the prescription cap fired for this set (`logSet.prescribedWeightCapped`). */
  prescribedWeightCapped?: boolean;
  /** The weight `openSet` prefilled, already snapped onto the equipment the set opened on
   *  (`logSet.plannedWeight_kg`). Only the clamp reads it — see `clampSnappedBenchmarkWeight_kg`. */
  prefilledWeight_kg?: number | undefined;
  /** True on the first set after a break whose layoff weight was stored as the path's target
   *  (#5617, `logSet.layoffPrescribed`). The benchmark is then the target itself, and only the clamp
   *  reads this. */
  layoffPrescribed?: boolean;
}

// each exception counts only when its weight is actually present — a flag with no weight falls
// through to the next rule, so both helpers below must agree on the same pair
function isBenchmarkCapped(args: BenchmarkWeightInput): boolean {
  return !!args.prescribedWeightCapped && args.cappedWeight_kg != null;
}

function isBenchmarkLayoffHeld(args: BenchmarkWeightInput): boolean {
  return args.layoffHeldWeightWithReducedReps && args.heldWeight_kg != null;
}

export interface LayoffHeldWeightInput {
  /** True while the loader's in-memory `reducedTargetReps_delta` marker still exists. The first set's
   *  conclusion deletes it, so only that conclusion ever sees it. */
  hasLoaderMarker: boolean;
  /** The set being concluded. */
  set: Log_WorkoutSet;
  /** The set's index within the exercise. */
  iSet: number;
  /** The exercise's sets this session BEFORE this one, first set first. */
  earlierSets: Log_WorkoutSet[];
  /** The path's `target_weight_kg` as it stands now. */
  currentTargetWeight_kg: number | undefined;
}

/**
 * Whether this set is judged against the weight a training break HELD (#3924) rather than the path's
 * climbing target. The loader's marker says so for the conclusion that sees it. A correction re-runs
 * `concludeSet` after the marker is gone, so it reads the fact back off the set (df-78bb051f6709).
 *
 * #5685: a FOLLOW-UP set in that session is held too, until something this session moves the weight:
 * an earlier set earned a target hit, or the path's target no longer matches what the first set was
 * prescribed (an accepted deload). Both are needed — a first-set target hit can leave the target at the
 * same number. Why, with the numbers: `design-docs/reactive-deload.md`, the #3924 section.
 */
export function isSetJudgedAgainstLayoffHeldWeight(args: LayoffHeldWeightInput): boolean {
  if (args.hasLoaderMarker || args.set.layoffHeldWeightWithReducedReps) {
    return true;
  }
  const firstSet = args.earlierSets[0];
  if (args.iSet === 0 || !firstSet?.layoffHeldWeightWithReducedReps) {
    return false;
  }
  const weightMovedByEarlierHit = args.earlierSets.some(earlier => earlier.setSuccess === SetSuccess.TargetReps);
  const targetMoved = args.currentTargetWeight_kg !== firstSet.planPathBackup?.target_weight_kg;
  return !weightMovedByEarlierHit && !targetMoved;
}

export interface BenchmarkInputForSetArgs {
  /** The set's index within the exercise. */
  iSet: number;
  /** The set being concluded. */
  set: Log_WorkoutSet;
  /** The exercise's first set this session, or undefined when there is none. */
  firstSet: Log_WorkoutSet | undefined;
  /** The path the set is judged on. */
  planPath: Plan_ExercisePath;
  /** The answer of `isSetJudgedAgainstLayoffHeldWeight` for this set. */
  layoffHeld: boolean;
}

/** Everything `benchmarkTargetWeight_kg` and `clampSnappedBenchmarkWeight_kg` need for one set. */
export function benchmarkInputForSet(args: BenchmarkInputForSetArgs): BenchmarkWeightInput {
  const { iSet, set, firstSet, planPath, layoffHeld } = args;
  // #5685: a held follow-up reads the held weight and what the screen showed off the FIRST set's
  // records. An earlier set this session can overwrite the live `lastStats`, and a correction restores
  // the follow-up's own copy of it, so the first set's record is the one value both conclusions see.
  const heldFollowUp = iSet > 0 && layoffHeld && firstSet != null;
  return {
    targetWeight_kg: planPath.target_weight_kg,
    // issue #3924: in the rep-fallback layoff the weight was held at the last lifted weight, so the
    // benchmark we judge the logged set against is that held weight, not the higher climbing target.
    heldWeight_kg: heldFollowUp
      ? firstSet.planPathBackup?.lastStats?.weight_kg ?? planPath.lastStats?.weight_kg
      : planPath.lastStats?.weight_kg,
    layoffHeldWeightWithReducedReps: layoffHeld,
    // #5176: when the prescription was capped, the lowered weight is what the user was shown
    // and prefilled with, so it is what they must be judged against — otherwise lifting exactly
    // what the app asked for is scored a miss and offers a deload, which is the very loop the
    // cap exists to end. Read from the SET, not from `e.prescriptionCap`, so a correction made
    // in a later session is judged against what that session actually prescribed. A follow-up
    // set's own prefill is the previous set's logged weight, so it takes the first set's too.
    cappedWeight_kg: cappedBenchmarkWeight_kg(iSet, set.plannedWeight_kg, firstSet?.plannedWeight_kg),
    prescribedWeightCapped: !!set.prescribedWeightCapped,
    // #5617: only the first set carries it, and only the first set's prefill is what was asked
    layoffPrescribed: iSet === 0 && !!set.layoffPrescribed,
    // only the FIRST set's prefill is the held weight snapped onto the equipment; a follow-up
    // set is prefilled with the previous set's logged weight, which is no ceiling at all
    prefilledWeight_kg: iSet === 0 ? set.plannedWeight_kg : heldFollowUp ? firstSet.plannedWeight_kg : undefined,
  };
}

/**
 * The capped weight a set is judged against in a capped session (#5176, df-3e9a1e0fc5ad).
 *
 * The first set's `plannedWeight_kg` IS the capped weight: `openSet` prefills it from the lowered
 * target. A follow-up set's `plannedWeight_kg` is not — `openSet` prefills a follow-up with the
 * previous set's LOGGED weight. Judging against that would let a user capped at 37 kg who lifts
 * 30 kg on set 1 have set 2 at 30 × target reps counted as hitting the target.
 *
 * So a follow-up is judged against the higher of the first set's planned weight and its own. The
 * first set's weight stops the yardstick sliding below what the session asked for. Its own weight
 * keeps a higher ask in force: when an earlier set hit the target, `openSet` prefills the follow-up
 * with the raised path target, and that raised weight is then what the app asked for.
 *
 * Returns undefined when neither weight is known, so the flag falls through to the path target
 * exactly as `benchmarkTargetWeight_kg` does for a set with no recorded weight.
 */
export function cappedBenchmarkWeight_kg(iSet: number, setPlannedWeight_kg: number | undefined, firstSetPlannedWeight_kg: number | undefined): number | undefined {
  if (iSet === 0 || firstSetPlannedWeight_kg == null) {
    return setPlannedWeight_kg;
  }
  if (setPlannedWeight_kg == null) {
    return firstSetPlannedWeight_kg;
  }
  return Math.max(setPlannedWeight_kg, firstSetPlannedWeight_kg);
}

/**
 * The benchmark after it has been snapped onto the equipment attached at conclusion, with one
 * guarantee added: when the benchmark is a weight the user was explicitly ASKED to lift — the capped
 * weight (#5176) or the layoff held weight (#3924) — the snapped value is never ABOVE it.
 *
 * `snapped_kg` is `adjustWeight(benchmark, 'round', equipment)`. That snap goes to the nearest rung of
 * whatever equipment is attached NOW, which need not be the equipment the set was opened on, so it can
 * raise an asked-for weight — and then lifting exactly what the app asked for scores a miss and offers
 * a deload. The layoff route is concrete: accepting a set the attached equipment cannot load invents a
 * machine stepping by this session's weight minus last session's, which in a layoff hold is 0 and falls
 * back to the 2.5 kg default, so a 37 kg held weight would snap to 37.5.
 *
 * "Asked" means what the screen showed. For a layoff set that is the prefilled weight — the held
 * weight snapped onto the equipment the set opened on — not the raw held weight, which can sit off
 * that grid: held 37 kg on a 2.5 kg machine is shown as 37.5, and clamping to 37 would read a set at
 * the shown 37.5 as added load. The raw held weight is the ceiling only when nothing was prefilled.
 * A capped weight IS the prefilled weight already.
 *
 * It CLAMPS rather than snapping with `round_down`, which would put the yardstick a full step below the
 * asked-for weight every time. The ordinary climbing target is routinely off-grid and keeps the plain
 * two-way `round`. Why, and what the clamp does not promise: `design-docs/reactive-deload.md`, the #5176
 * section.
 */
export function clampSnappedBenchmarkWeight_kg(args: BenchmarkWeightInput, snapped_kg: number | undefined): number | undefined {
  let asked_kg: number | undefined;
  if (isBenchmarkCapped(args)) {
    asked_kg = args.cappedWeight_kg;
  } else if (isBenchmarkLayoffHeld(args)) {
    asked_kg = args.prefilledWeight_kg ?? args.heldWeight_kg;
  } else if (args.layoffPrescribed && args.targetWeight_kg != null) {
    // #5617: the stored target IS the weight the layoff asked for
    asked_kg = args.prefilledWeight_kg ?? args.targetWeight_kg;
  }
  if (snapped_kg == null || asked_kg == null) {
    return snapped_kg;
  }
  return Math.min(snapped_kg, asked_kg);
}

/**
 * How many reps of benefit of the doubt the prescription cap gives the user beyond what their best
 * recent set actually proved (#5176).
 *
 * Without a credit the cap forbids progression outright, because the two strength functions are
 * exact inverses. Why two, and the numbers: `design-docs/reactive-deload.md`, the #5176 section.
 */
export const PRESCRIPTION_CAP_REP_CREDIT = 2;

export interface PrescriptionCapInput {
  /** Raw stats of the best usable set the user produced across their last few sessions of this
   *  exercise, as chosen by `bestStrengthSetAcrossSessions`.
   *
   *  Across SESSIONS, not within the latest one — see `PRESCRIPTION_CAP_SESSION_WINDOW`. Judging by
   *  the most recent session alone clamps the next prescription on the strength of a single bad day,
   *  and the session that triggers a reactive deload is a bad one by definition.
   *
   *  The RAW recorded weight/reps/bw_pc — never a stored strength figure, which is a high-water mark
   *  and cannot come down (see #5174). */
  bestRecentSet: SetStats | undefined;
  /** The weight the plan wants to prescribe, before any equipment rounding. */
  plannedWeight_kg: number | undefined;
  /** How many reps that prescription asks for. */
  prescribedReps: number | undefined;
  /** TODAY's bodyweight, not the bodyweight at the time of the recorded set. */
  bodyWeight_kg: number;
  /** The exercise's own bodyweight share, used when the recorded set carries none (`SetStats.bw_pc`
   *  is optional and old sets may predate it). */
  exerciseBw_pc: number;
  equipment: Equipment | undefined;
}

export interface PrescriptionCapResult {
  /** The weight to prescribe instead, snapped onto the equipment grid and never above `ceiling_kg`. */
  cappedWeight_kg: number;
  /** The unsnapped ceiling. NEGATIVE means the prescribed reps are impossible even with no added
   *  weight at all — a real answer, not an error, and the same signal `deriveRmTestPathTarget`
   *  reports as `isBelowZero`. The caller reports it rather than hiding it. */
  ceiling_kg: number;
  /** The weight the plan wanted BEFORE the cap, unsnapped. Kept so the cap can be snapped again
   *  onto equipment the page attaches after the load — see `snapPrescriptionCap_kg`. */
  plannedWeight_kg: number;
}

/**
 * The heaviest weight at which the prescribed number of reps is still possible, given what the user
 * most recently proved they can do — or `undefined` when the plan's own weight already sits at or
 * below it and nothing needs to change (#5176).
 *
 * Why this exists: the app could prescribe a weight above its own estimate of what the user can lift
 * ONCE, and keep prescribing it every session, because nothing in the progression loop ever lowers a
 * target that the user never succeeds at. The reported case (#4556) asked for 67.5 lb x 12 reps
 * against a 56.4 lb one-rep max.
 *
 * The one-rep max is recomputed here from the raw recorded set with TODAY's bodyweight. It is
 * deliberately NOT read from any stored figure: both `planPath.lastStats` and
 * `ExerciseStat.estimated1RM_net_kg` only ever move upward, so for the failing user this is meant to
 * protect they stay pinned to a months-old success and the ceiling never falls.
 */
export function capPrescribedWeight_kg(input: PrescriptionCapInput): PrescriptionCapResult | undefined {
  const { bestRecentSet, plannedWeight_kg, prescribedReps, bodyWeight_kg, exerciseBw_pc, equipment } = input;

  // if there is nothing to judge from, say nothing — a user with no history is not evidence of weakness
  if (!bestRecentSet || !Number.isFinite(bestRecentSet.weight_kg) || !Number.isFinite(bestRecentSet.reps)) {
    return undefined;
  }
  const provenReps = Math.round(bestRecentSet.reps);
  if (provenReps < 1) {
    return undefined;
  }
  // if there is nothing to judge — no weight or no rep count on the prescription — leave it alone
  if (!Number.isFinite(plannedWeight_kg) || !Number.isFinite(prescribedReps) || Math.round(prescribedReps) < 1) {
    return undefined;
  }

  // old sets may predate `bw_pc` being recorded, so fall back to the exercise's own share
  const bw_pc = bestRecentSet.bw_pc ?? exerciseBw_pc;
  // what the user could lift once, if they had managed PRESCRIPTION_CAP_REP_CREDIT more reps than
  // they actually did — computed with TODAY's bodyweight, never the bodyweight of that day
  const creditedOneRepMax_net_kg = calculate1RM(
    bestRecentSet.weight_kg, provenReps + PRESCRIPTION_CAP_REP_CREDIT, bodyWeight_kg, bw_pc,
  ).estimated1rm_net_kg;
  // ...and the added weight at which THIS prescription's rep count is still reachable from it
  const ceiling_kg = estimateWeightForReps(creditedOneRepMax_net_kg, prescribedReps, bodyWeight_kg, bw_pc);
  if (ceiling_kg === undefined) {
    return undefined;
  }

  // if the plan already sits at or below what the user can do, there is nothing to cap. The
  // tolerance is what keeps this off the healthy path: the two strength functions round-trip
  // EXACTLY, so a prescription repeating the set it came from lands on the ceiling to the last bit
  // and a bare `>` comparison would fire on float dust.
  if (plannedWeight_kg - ceiling_kg <= WEIGHT_GRID_TOLERANCE_KG) {
    return undefined;
  }

  const cappedWeight_kg = snapPrescriptionCap_kg(ceiling_kg, plannedWeight_kg, equipment);
  if (cappedWeight_kg === undefined) {
    return undefined;
  }

  return { cappedWeight_kg, ceiling_kg, plannedWeight_kg };
}

/**
 * Snap a prescription cap's ceiling onto an equipment grid, never above the ceiling — or
 * `undefined` when the snapped cap would not change the number the user SEES (#5176).
 *
 * Its own function because the cap is snapped twice for some exercises. The loader caps before the
 * page has attached equipment to an exercise whose equipment was never chosen, and with no equipment
 * the snap does nothing. The page snaps again once it has attached that equipment.
 */
export function snapPrescriptionCap_kg(
  ceiling_kg: number,
  plannedWeight_kg: number,
  equipment: Equipment | undefined,
): number | undefined {
  // a negative ceiling is a real answer — the reps are impossible even unloaded — but it is not a
  // weight anybody can load, so the prescription floors at zero added weight. The caller reports
  // the below-zero case from `ceiling_kg` rather than letting the floor hide it.
  const loadableCeiling_kg = Math.max(ceiling_kg, 0);
  let cappedWeight_kg = adjustWeight(loadableCeiling_kg, 'round', equipment).weight_kg;
  // 'round' snaps to the NEAREST rung, which can sit above the ceiling. Step down when it really
  // does — but only past the grid tolerance, so an exact round-trip is not dropped a whole step for
  // a fraction of a gram.
  if (cappedWeight_kg - loadableCeiling_kg > WEIGHT_GRID_TOLERANCE_KG) {
    cappedWeight_kg = adjustWeight(cappedWeight_kg, 'step_down', equipment).weight_kg;
  }
  cappedWeight_kg = Math.max(cappedWeight_kg, 0);

  // Say nothing unless this actually changes the number the user will SEE. That comparison has to be
  // against the planned weight snapped to the same grid, because a plan target is routinely off-grid
  // — it comes out of a percentage of an estimated one-rep max — and the loader rounds it before
  // showing it. Comparing against the raw value would let a sub-step cut "fire" while the displayed
  // number stayed identical, and the app would announce a lowering it did not make.
  //
  // This also covers the case where the ceiling falls below the equipment's lightest setting:
  // `adjustWeight` clamps the step count at zero, so the capped weight snaps back up to that floor.
  const roundedPlannedWeight_kg = adjustWeight(plannedWeight_kg, 'round', equipment).weight_kg;
  if (roundedPlannedWeight_kg - cappedWeight_kg <= WEIGHT_GRID_TOLERANCE_KG) {
    return undefined;
  }

  return cappedWeight_kg;
}

/** What snapping a cap again onto newly attached equipment did to it. */
export enum PrescriptionCapResnap {
  /** the snap lands where the cap already was, or there is still no equipment — change nothing */
  Unchanged = 'unchanged',
  /** the cap moved onto the equipment grid — prescribe `targetWeight_kg` */
  Moved = 'moved',
  /** on this grid the cut does not change the number the user sees, so it is no cap at all —
   *  drop it and prescribe the plan's own weight, `targetWeight_kg` */
  Dropped = 'dropped',
}

/**
 * Snap an existing cap again, onto equipment attached after the cap was worked out (#5176).
 *
 * The loader caps before the page has created equipment for an exercise whose equipment was never
 * chosen, so that first snap had no grid. See `snapPrescriptionCap_kg`.
 */
export function resnapPrescriptionCap(
  cap: PrescriptionCapResult,
  equipment: Equipment | undefined,
): { outcome: PrescriptionCapResnap; targetWeight_kg: number } {
  // if there is still no grid to snap onto, there is nothing to redo
  if (!equipment) {
    return { outcome: PrescriptionCapResnap.Unchanged, targetWeight_kg: cap.cappedWeight_kg };
  }
  const cappedWeight_kg = snapPrescriptionCap_kg(cap.ceiling_kg, cap.plannedWeight_kg, equipment);
  if (cappedWeight_kg === undefined) {
    return { outcome: PrescriptionCapResnap.Dropped, targetWeight_kg: cap.plannedWeight_kg };
  }
  // if the snap lands where the cap already was, leave it — this is every exercise that already
  // had its equipment when the cap ran
  if (Math.abs(cappedWeight_kg - cap.cappedWeight_kg) <= WEIGHT_GRID_TOLERANCE_KG) {
    return { outcome: PrescriptionCapResnap.Unchanged, targetWeight_kg: cap.cappedWeight_kg };
  }
  return { outcome: PrescriptionCapResnap.Moved, targetWeight_kg: cappedWeight_kg };
}

/**
 * How many recent sessions of an exercise the prescription cap looks back over (#5176).
 *
 * One session is not enough: the session that triggers a reactive deload is a bad one by definition,
 * so judging by it alone clamps the next prescription below what the user just proved. The E2E loop
 * suite catches exactly that. The worked numbers, and what widening the window costs:
 * `design-docs/reactive-deload.md`, the #5176 section.
 */
export const PRESCRIPTION_CAP_SESSION_WINDOW = 3;

/** Set types that say nothing about what the user can do, so the cap must never read them.
 *  A `Deload` set is prescribed at 60% of the one-rep max and a `Dropset` at 90% of the set before
 *  it — both deliberately submaximal. Reading either would cap a user brutally the session after
 *  any deload, on evidence that was never about their limit. */
const SUBMAXIMAL_SET_TYPES: ReadonlySet<SetType> = new Set([SetType.Deload, SetType.Dropset]);

/**
 * The set from one session that best represents what the user can currently do, or `undefined` when
 * the session holds nothing usable (#5176).
 *
 * Deliberately NOT filtered by success — that filter is what makes `planPath.lastStats` a high-water
 * mark, and a failed set is still evidence of current strength. Equally deliberately not simply the
 * LAST set, which is usually a deload or a dropset and so prescribed below the user's limit.
 *
 * Not a duplicate of `bestStrengthOfSession_net_kg` (#5174) a few functions below: this returns the
 * raw SET (the cap needs the rep count to apply its credit), recomputes with TODAY's bodyweight
 * rather than reading the stored figure, and skips deload and dropset sets. Do not collapse them.
 */
export function bestStrengthSetOfSession(
  sets: Log_WorkoutSet[] | undefined,
  bodyWeight_kg: number,
  exerciseBw_pc: number,
): SetStats | undefined {
  let best: SetStats | undefined;
  let bestRm_net_kg = -Infinity;
  for (const set of sets ?? []) {
    // only a set the user actually completed says anything; skipped and downregulated ones do not
    if (!set || set.state !== LogRecordState.Done || !set.stats) {
      continue;
    }
    if (SUBMAXIMAL_SET_TYPES.has(set.setType)) {
      continue;
    }
    const { weight_kg, reps } = set.stats;
    if (!Number.isFinite(weight_kg) || !Number.isFinite(reps) || Math.round(reps) < 1) {
      continue;
    }
    // recompute rather than trust the stored figure: it was derived with the bodyweight of that day,
    // and on a bodyweight exercise that makes it incomparable with today's
    const bw_pc = set.stats.bw_pc ?? exerciseBw_pc;
    const rm_net_kg = calculate1RM(weight_kg, reps, bodyWeight_kg, bw_pc).estimated1rm_net_kg;
    if (rm_net_kg > bestRm_net_kg) {
      bestRm_net_kg = rm_net_kg;
      best = set.stats;
    }
  }
  return best;
}

/**
 * The best set the user produced across their last few sessions of an exercise — what the
 * prescription cap judges them by (#5176).
 *
 * Looking at only the most recent session is wrong, and the E2E loop suite proves it: the session
 * that triggers a reactive deload is by definition a bad one, so a cap reading only that session
 * clamps the very next prescription far below what the user did the week before. See
 * `PRESCRIPTION_CAP_SESSION_WINDOW` for the worked numbers.
 */
export function bestStrengthSetAcrossSessions(
  /** Each entry is one session's sets, MOST RECENT FIRST. Reading stops once
   *  `PRESCRIPTION_CAP_SESSION_WINDOW` sessions have YIELDED a usable set. */
  sessions: (Log_WorkoutSet[] | undefined)[],
  bodyWeight_kg: number,
  exerciseBw_pc: number,
): SetStats | undefined {
  let best: SetStats | undefined;
  let bestRm_net_kg = -Infinity;
  let sessionsRead = 0;
  for (const sets of sessions) {
    if (sessionsRead >= PRESCRIPTION_CAP_SESSION_WINDOW) break;
    const candidate = bestStrengthSetOfSession(sets, bodyWeight_kg, exerciseBw_pc);
    // A session holding nothing the cap can read — abandoned with only prefilled sets, dated in the
    // future, or entirely deload and dropset work — must NOT spend a slot in the window. It carries
    // no evidence either way, and letting it count would let three such sessions in front of the
    // real history switch the cap off silently.
    if (!candidate) continue;
    sessionsRead++;
    const rm_net_kg = calculate1RM(
      candidate.weight_kg, candidate.reps, bodyWeight_kg, candidate.bw_pc ?? exerciseBw_pc,
    ).estimated1rm_net_kg;
    if (rm_net_kg > bestRm_net_kg) {
      bestRm_net_kg = rm_net_kg;
      best = candidate;
    }
  }
  return best;
}


export interface Regular1stDecisionInput {
  /** The logged first set. `estimated1rm_net_kg` must already be populated (the component calls
   *  `estimate1RM` before deciding). */
  setStats: SetStats;
  /** Prescribed target weight, rounded to the user's equipment. May be undefined if the path has no
   *  target yet (then the weight-based branches never fire, matching the old `>= undefined` checks). */
  roundedTargetWeight_kg: number | undefined;
  /** Prescribed target reps for this path. */
  targetReps: number;
  /** Armed minimum-rep floor for this path. */
  minReps: number;
  /** The scheme's minimum acceptable reps for this path's intensity, from
   *  `getMinTargetRepsForIntensity` (a per-intensity value, 3-9 across the defined schemes, default 4).
   *  Distinct from `minReps`, which is the armed progression floor. Used by the #3720 Heavier-block
   *  rule: a strictly-heavier-than-prescribed set whose reps reach this minimum is progress (rule 2);
   *  below it is the failed-set / "weight too heavy" path. */
  schemeMinReps: number;
  /** Previous workout's high-water benchmark (its strongest TargetReps/MinReps set, picked by
   *  `previousSessionBaselineSet`), if any. */
  previousFirstSetStats?: SetStats;
  /** The plan path's stored `lastStats` baseline, if any. */
  lastStats?: SetStats;
  /** The user's bodyweight (kg), needed to re-estimate 1RM for bodyweight-loaded exercises. */
  bodyWeight_kg: number;
}

/**
 * The set of the previous session that this session's first set is judged against: its STRONGEST
 * finished set marked `TargetReps` or `MinReps`, by net estimated 1RM. A later set wins a tie.
 * Why not the last such set: `design-docs/reactive-deload.md`, "Which set of the previous session is
 * the yardstick" (df-808f4b93dc06).
 *
 * A set whose stored estimate is missing or unusable (an old or damaged document) cannot be ranked.
 * When no set can be ranked, the last successful set is the answer, as it was before.
 */
export function previousSessionBaselineSet(sets: Log_WorkoutSet[]): Log_WorkoutSet | undefined {
  let lastSuccessful: Log_WorkoutSet | undefined;
  let strongest: Log_WorkoutSet | undefined;
  for (const set of sets) {
    // only a finished, successful set with a weight says what the user could do
    if (!set || set.state !== LogRecordState.Done || set.stats?.weight_kg == null ||
        (set.setSuccess !== SetSuccess.TargetReps && set.setSuccess !== SetSuccess.MinReps)) {
      continue;
    }
    lastSuccessful = set;

    const estimate_net_kg = set.stats.estimated1rm_net_kg;
    if (!isFinite(estimate_net_kg) || estimate_net_kg <= 0) {
      continue;
    }
    // ">=" so a later set of equal strength wins, which keeps an all-equal session as it was
    if (strongest === undefined || estimate_net_kg >= strongest.stats.estimated1rm_net_kg) {
      strongest = set;
    }
  }
  return strongest ?? lastSuccessful;
}

/** Check if a stored net estimated 1RM can be compared at all: a missing, non-finite or non-positive
 *  one comes from an old or damaged document. */
function isUsableEstimate_net_kg(estimate_net_kg: number | null | undefined): estimate_net_kg is number {
  return estimate_net_kg != null && isFinite(estimate_net_kg) && estimate_net_kg > 0;
}

/**
 * The set of the previous session that this session's first set must beat to be praised as "You
 * progressed!" (#6447): its STRONGEST finished set by net estimated 1RM, whatever its success mark,
 * with a later set winning a tie. A failed set counts, so a session whose deload the user accepted
 * is compared with what the user really did there (owner decision 2026-10-12). Unlike
 * `previousSessionBaselineSet`, this is display only: the progression decision never reads it.
 *
 * Answers undefined when the session holds no finished set with a usable estimate, for example a
 * workout the user skipped; the caller then looks further back.
 */
export function previousSessionBestAttempt(sets: Log_WorkoutSet[]): Log_WorkoutSet | undefined {
  let strongest: Log_WorkoutSet | undefined;
  for (const set of sets) {
    // only a finished set with a usable estimate says what the user did
    if (!set || set.state !== LogRecordState.Done || !isUsableEstimate_net_kg(set.stats?.estimated1rm_net_kg)) {
      continue;
    }
    const estimate_net_kg = set.stats.estimated1rm_net_kg;
    // ">=" so a later set of equal strength wins, as in previousSessionBaselineSet
    if (strongest === undefined || estimate_net_kg >= strongest.stats.estimated1rm_net_kg) {
      strongest = set;
    }
  }
  return strongest;
}

/**
 * How long tapping Done on a first set waits for the previous-workout read before judging the set
 * without it (df-3a4a75620292). That read runs in the background and can take seconds on a large
 * database, and without it the #6309 collapse check is skipped. Long enough for those slow reads,
 * short enough that a stuck read does not hang the button. MIRRORED in
 * `design-docs/reactive-deload.md`, "When the previous workout has not loaded yet".
 */
export const PREVIOUS_SESSION_WAIT_MS = 5000;

/** How a wait for the previous-workout read ended. */
export enum PreviousSessionWait {
  /** No read was ever started for this exercise, so there was nothing to wait for. */
  NoRead = 'no-read',
  /** The read finished (or failed, which its starter reports) within the limit. */
  Finished = 'finished',
  /** The limit ran out first; the set is judged without the previous workout. */
  TimedOut = 'timed-out',
}

/** The two timer calls `waitForPreviousSessionReadAsync` needs, so a test can drive the clock. */
export interface WaitTimers {
  setTimeout(cb: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const REAL_WAIT_TIMERS: WaitTimers = {
  setTimeout: (cb, ms) => setTimeout(cb, ms),
  clearTimeout: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/**
 * Waits for the previous-workout read, for at most `limit_ms`. Never throws: a failed read is
 * reported where it was started, and ending the wait is all that matters here. A read that already
 * finished answers on the next microtask, so a tap after it costs nothing.
 */
export function waitForPreviousSessionReadAsync(read: Promise<void> | undefined, limit_ms: number, timers: WaitTimers = REAL_WAIT_TIMERS): Promise<PreviousSessionWait> {
  if (!read) {
    return Promise.resolve(PreviousSessionWait.NoRead);
  }
  return new Promise(resolve => {
    let settled = false;
    const timer = timers.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(PreviousSessionWait.TimedOut);
    }, limit_ms);
    const finish = () => {
      if (settled) return;
      settled = true;
      timers.clearTimeout(timer);
      resolve(PreviousSessionWait.Finished);
    };
    read.then(finish, finish);
  });
}

/** Check if this first set is an objective regression vs the previous session: the net estimated
 *  1RM dropped while the weight did NOT increase. Used to stop the post-progression `min_reps = 1`
 *  grace from masking a genuine same-weight benchmark collapse, which would otherwise be classified
 *  as `MinReps` success and suppress the reactive deload (#3349). */
function isClearRegression(setStats: SetStats, previousFirstSetStats: SetStats | undefined, lastStats: SetStats | undefined): boolean {
  // prefer fresh data from the actual previous workout over stale planPath.lastStats
  const prev = previousFirstSetStats || lastStats;

  // no baseline to compare against → never treat as a regression
  if (!prev) {
    return false;
  }

  // weight went UP vs last time → legitimate progression grace, not a regression
  if (setStats.weight_kg > prev.weight_kg) {
    return false;
  }

  // identical set → not a regression
  if (setStats.reps === prev.reps && setStats.weight_kg === prev.weight_kg) {
    return false;
  }

  // same-or-lower weight AND net 1RM dropped → an objective regression
  return setStats.estimated1rm_net_kg < prev.estimated1rm_net_kg;
}

/**
 * How far below the previous session's high-water set a heavier-than-prescribed set's estimated 1RM
 * may fall and still count as progress under rule 2 (#6309, owner decision 2026-10-09). A normal dip
 * (65x11 → 65x10, 2.9% lower) stays progress, as #3720 intended; a collapse (65x11 → 65x7, 11% lower)
 * goes on to the baseline-aware steps and usually the deload question. MIRRORED in
 * `design-docs/reactive-deload.md`, "Related: #3349 same-weight collapse".
 */
export const HEAVIER_SET_COLLAPSE_TOLERANCE = 0.05;

/** Check if this set collapsed against the previous session's high-water set: not heavier than it,
 *  and its net estimated 1RM more than `HEAVIER_SET_COLLAPSE_TOLERANCE` below it (#6309). Only the
 *  previous session counts, never the path's `lastStats`: after an accepted deload `lastStats` still
 *  holds the set from before the collapse, and judging a recovery against it would ask again. */
function isCollapseAgainstPreviousSession(setStats: SetStats, previousFirstSetStats: SetStats | undefined): boolean {
  const prev = previousFirstSetStats;

  // no previous session to compare against → keep the heavier-set rule
  if (!prev) {
    return false;
  }

  // weight went UP vs the previous session → going up on the bar is progress (#3720)
  if (setStats.weight_kg > prev.weight_kg) {
    return false;
  }

  return setStats.estimated1rm_net_kg < prev.estimated1rm_net_kg * (1 - HEAVIER_SET_COLLAPSE_TOLERANCE);
}

/** Check if this first set's net estimated 1RM is strictly higher than the previous session's best
 *  attempt (`previousSessionBestAttempt`, #6447). Only then does the confirmation say "You
 *  progressed!". A missing previous session, or an unusable estimate on either side, means "not
 *  sure", which is not a gain (owner decision 2026-10-12). Display only: the decision chain above
 *  does not read it. */
export function isStrengthGainOverPreviousSession(setStats: SetStats, previousAttemptStats: SetStats | undefined): boolean {
  const today_net_kg = setStats.estimated1rm_net_kg;
  const previous_net_kg = previousAttemptStats?.estimated1rm_net_kg;

  // if either estimate is missing or unusable, we cannot say the set got stronger
  if (!isUsableEstimate_net_kg(today_net_kg) || !isUsableEstimate_net_kg(previous_net_kg)) {
    return false;
  }
  return today_net_kg > previous_net_kg;
}

/** Check if this set is guaranteed to NOT be a deload based on comparison to the previous workout.
 *  Returns true when weight went UP, or reps went UP at the same weight. Returns false when there is
 *  no previous-workout high-water mark — `isWeightImproved` then handles it with the lastStats fallback. */
function isGuaranteedNonDeload(setStats: SetStats, previousFirstSetStats: SetStats | undefined): boolean {
  const prev = previousFirstSetStats;

  // if no high water mark from previous workout (e.g. all sets failed/deloaded), fall through
  if (!prev) {
    return false;
  }

  // if user went heavier than previous workout
  if (setStats.weight_kg > prev.weight_kg) {
    return true;
  }

  // if same weight and more reps than previous workout
  if (setStats.weight_kg === prev.weight_kg && setStats.reps > prev.reps) {
    return true;
  }

  return false;
}

/** Check if effective RM improved comparing to the last time. Used for the weird cases — the user
 *  manually lowers the weight or hits fewer reps, but perhaps got heavier (higher BW) so a
 *  bodyweight exercise is still progressing — and for the "one more rep would have overshot the old
 *  1RM" reinterpretation. */
function isWeightImproved(setStats: SetStats, previousFirstSetStats: SetStats | undefined, lastStats: SetStats | undefined, bodyWeight_kg: number): boolean {
  // prefer fresh data from actual previous workout over stale planPath.lastStats
  const prev = previousFirstSetStats || lastStats;

  // can't compare anything if we don't have a relevant set stored
  if (!prev) {
    return false;
  }

  // if new net is higher than old net, this is progress for sure
  if (setStats.estimated1rm_net_kg > prev.estimated1rm_net_kg) {
    return true;
  }

  // if the set is identical to the one from last workout — that's certainly not progress
  if (setStats.reps === prev.reps && setStats.weight_kg === prev.weight_kg) {
    return false;
  }

  // if adding one more rep to this set would overshoot the previous 1RM, we assume it is still
  // progress, since the user probably couldn't have done better
  const extraRepStats: SetStats = { ...setStats };
  extraRepStats.reps += 1;
  estimate1RM(extraRepStats, bodyWeight_kg); // this is the 1RM we'd get with one more rep

  if (extraRepStats.estimated1rm_net_kg > prev.estimated1rm_net_kg) {
    return true;
  }

  // we couldn't reinterpret this set as an improvement in any way
  return false;
}

/**
 * Classify the logged Regular1st benchmark into one of the three progression outcomes. Mirrors the
 * decision chain that used to live inline in `concludeSet`:
 *   1. hit target weight AND target reps                          → targetHit
 *   2. STRICTLY heavier than prescribed AND reps ≥ scheme min     → minReps   (#3720, Alen's rule 2)
 *      AND no collapse vs the previous session                               (#6309)
 *   3. hit target weight AND cleared the floor AND not a regress  → minReps
 *   4. weight/reps up vs the previous workout's high-water        → minReps
 *   5. effective 1RM improved (weight down + more reps, BW gain)  → targetHit or minReps
 *   6. none of the above                                          → deload
 *
 * `isViaWeightImproved` is true only for outcome 5, so the caller knows to surface the
 * "lower than recommended weight" hint that branch produced.
 */
export function classifyRegular1stProgression(input: Regular1stDecisionInput): { outcome: Regular1stOutcome; isViaWeightImproved: boolean } {
  const { setStats, roundedTargetWeight_kg, targetReps, minReps, schemeMinReps, previousFirstSetStats, lastStats, bodyWeight_kg } = input;
  const hitTargetWeight = roundedTargetWeight_kg != null && setStats.weight_kg >= roundedTargetWeight_kg;

  // 1. hit the target weight AND target reps → linear progression
  if (hitTargetWeight && setStats.reps >= targetReps) {
    return { outcome: Regular1stOutcome.TargetHit, isViaWeightImproved: false };
  }

  // 2. (#3720, Alen's rule 2) the whole "Heavier-than-prescribed" block is progress, never a deload.
  //    A set strictly above the prescribed weight whose reps reach the per-scheme minimum
  //    (schemeMinReps) means the user chose to add load — a user-driven weight jump must read the same
  //    as an app-driven one, so landing under the target reps does NOT deload them. This is prescription-
  //    relative on purpose: the estimated-1RM column does not gate the Heavier block, so a heavier set
  //    that is a touch lower on 1RM (e.g. 200x14 vs prescribed 190x16) is still progress. Reps BELOW
  //    schemeMinReps do NOT take this rule — they fall through to the baseline-aware steps below (the
  //    "weight too heavy / failed-set" region of the grid), which usually deload but can still pass via
  //    the raw-reps floor when the armed min_reps is in its post-progression grace.
  //    #6309: nor does a set that collapsed against the previous session at that heavier weight
  //    (e.g. 65x11 then 65x7 over a 62.5 prescription) — rule 2 never looked back, so a user who always
  //    loads above the prescription could not be offered a deload at all.
  if (roundedTargetWeight_kg != null && setStats.weight_kg > roundedTargetWeight_kg && setStats.reps >= schemeMinReps
    && !isCollapseAgainstPreviousSession(setStats, previousFirstSetStats)) {
    return { outcome: Regular1stOutcome.MinReps, isViaWeightImproved: false };
  }

  // 3. hit the target weight and cleared the rep floor by raw reps, and it isn't a clear regression
  //    (the `!isClearRegression` guard stops the post-progression `min_reps = 1` grace from swallowing
  //    a genuine same-weight benchmark collapse — see #3349)
  if (hitTargetWeight && setStats.reps >= minReps && !isClearRegression(setStats, previousFirstSetStats, lastStats)) {
    return { outcome: Regular1stOutcome.MinReps, isViaWeightImproved: false };
  }

  // 4. weight UP, reps UP, or no previous data → guaranteed non-deload
  if (isGuaranteedNonDeload(setStats, previousFirstSetStats)) {
    return { outcome: Regular1stOutcome.MinReps, isViaWeightImproved: false };
  }

  // 5. effective RM improved in fact (e.g. BW change, weight down with more reps)
  if (isWeightImproved(setStats, previousFirstSetStats, lastStats, bodyWeight_kg)) {
    return { outcome: setStats.reps >= targetReps ? Regular1stOutcome.TargetHit : Regular1stOutcome.MinReps, isViaWeightImproved: true };
  }

  // 6. no sign of progress → reactive deload
  return { outcome: Regular1stOutcome.Deload, isViaWeightImproved: false };
}

/**
 * The most reps a set may report and still yield a usable estimated 1RM (#4165). Past this the
 * blend in `calculate1RM` is extrapolation, not estimate, and the load derived from it can exceed
 * the lifter's own one-rep max.
 *
 * MIRRORED in `design-docs/reactive-deload.md` (#4165), which carries the numbers and explains why
 * the set is refused rather than clamped. The E2E spec imports this constant rather than copying it.
 */
export const MAX_USABLE_STRENGTH_TEST_REPS = 20;

/**
 * The highest rep target the app ever prescribes (#5979, owner and coaches' decision: 5-30 reps is
 * the effective range for muscle growth). A progression that would pass it raises the weight one step
 * instead, or offers a switch when that step is too big — see `decideRepCeiling`.
 *
 * MIRRORED in `design-docs/reactive-deload.md` (#4165 part 6, #5979).
 */
export const MAX_TARGET_REPS = 30;

/** What the app does for a user whose rep target has reached `MAX_TARGET_REPS` (#5979). */
export enum RepCeilingAction {
  /** go up one weight step, back at the preferred rep target — applied without a question */
  Raise = 'raise',
  /** the next weight is too big a jump: switch to another exercise */
  Switch = 'switch',
}

export type RepCeilingProposal =
  | { action: RepCeilingAction.Raise; newWeight_kg: number; newTargetReps: number }
  | { action: RepCeilingAction.Switch };

/**
 * The load a set that BEAT its rep target should move to, or `undefined` for "do not move the
 * weight" — when the reps are too high for the estimate to be usable, or the equipment cannot go
 * heavier (#4165).
 *
 * Runs `calculate1RM` backwards to the determined rep target, floored at one loadable step. Shared
 * by the two places the rule fires — `decideTargetHitProgression` and
 * `WorkoutDetailPage.applyBeatTargetWeightAfterStrengthTest` — so they cannot drift.
 *
 * See `design-docs/reactive-deload.md` (#4165) for why each guard is there.
 */
export function deriveBeatTargetWeight_kg(args: {
  /** the weight the user just lifted */
  loggedWeight_kg: number;
  /** the reps the user actually performed — gates the usable-estimate gate below */
  loggedReps: number;
  /** the logged set's `stats.estimated1rm_net_kg` */
  loggedEstimated1rm_net_kg: number;
  /** the rep target the app determined — the reps the derived load should be good for */
  determinedTargetReps: number;
  /** the logged weight moved one loadable step up */
  currentPlusOneStep_kg: number;
  bodyWeight_kg: number;
  bw_pc: number;
  equipment: Equipment;
}): number | undefined {
  // a set this far past its target says nothing usable about the lifter's 1RM, so there is no load
  // to derive from it. The strength-test branch refuses such a set outright and never gets here. A
  // working set falls back to the ordinary Case-1/Case-2 comparison, which for reps this far above
  // the anchor takes Case 1 — one loadable step up, rep target pulled back to the anchor — because
  // the mirrored `adjustedTargetRepsForCase1` goes negative and makes Case 1 the smaller estimate.
  // That is exactly what the app did with such a set before #4165, so declining here is a return to
  // the old behaviour, not a new one.
  if (!Number.isFinite(args.loggedReps) || args.loggedReps > MAX_USABLE_STRENGTH_TEST_REPS) {
    return undefined;
  }

  const impliedLoad_kg = estimateWeightForReps(
    args.loggedEstimated1rm_net_kg,
    args.determinedTargetReps,
    args.bodyWeight_kg,
    args.bw_pc,
  );
  if (impliedLoad_kg === undefined) {
    return undefined;
  }

  const snappedImplied_kg = adjustWeight(impliedLoad_kg, 'round', args.equipment).weight_kg;
  const newWeight_kg = Math.max(snappedImplied_kg, args.currentPlusOneStep_kg);
  // if the equipment cannot make anything heavier, there is no move to recommend
  return newWeight_kg > args.loggedWeight_kg ? newWeight_kg : undefined;
}

/** Which way a set that beat its rep target moves the progression (#5927). */
export enum BeatTargetMove {
  /** a heavier weight, at the rep target the raise prescribes */
  Weight = 'weight',
  /** the same weight, one rep more than the set actually did */
  Reps = 'reps',
}

export interface BeatTargetProgression {
  move: BeatTargetMove;
  newTargetWeight_kg: number;
  newTargetReps: number;
  /** the preferred rep target to store, or undefined to leave whatever is stored alone */
  anchorRepsToSeed: number | undefined;
}

/**
 * How a set that BEAT its rep target moves the progression: a heavier weight or one more rep —
 * or `undefined` when no weight can be derived, so the caller falls back to its ordinary rule (#5927).
 *
 * Whichever is the smaller jump in estimated one-rep max wins (owner ruling, 2026-09-26: "if they
 * proved that they can achieve 10 instead of 8, next time we offer them target 11 if that is lower
 * jump than increasing the weight"). On coarse equipment one step can be +40%, and the #5176
 * prescription cap then takes such a raise back the next session while the rep target stays put, so
 * the user loops for ever. A raise that wins here can never be taken back by that cap judging by this
 * same set: the cap credits two extra reps, and this compares against one. The numbers:
 * `design-docs/reactive-deload.md`, the #4165 section.
 */
export function decideBeatTargetProgression(args: {
  loggedWeight_kg: number;
  loggedReps: number;
  loggedEstimated1rm_net_kg: number;
  /** the path's rep target, which the heavier load is derived for — exactly as before #5927 */
  determinedTargetReps: number;
  /** the rep target the caller will actually prescribe with a raise. The two sites differ: a working
   *  set pulls it back to the preferred target, while a strength test keeps the path's target, which
   *  `setTargetReps` may already have moved off the preferred one. */
  raisedTargetReps: number;
  /** the user's stored preferred rep target, or undefined when they have none yet */
  anchorReps: number | undefined;
  currentPlusOneStep_kg: number;
  bodyWeight_kg: number;
  bw_pc: number;
  equipment: Equipment;
}): BeatTargetProgression | undefined {
  const raisedWeight_kg = deriveBeatTargetWeight_kg({
    loggedWeight_kg: args.loggedWeight_kg,
    loggedReps: args.loggedReps,
    loggedEstimated1rm_net_kg: args.loggedEstimated1rm_net_kg,
    determinedTargetReps: args.determinedTargetReps,
    currentPlusOneStep_kg: args.currentPlusOneStep_kg,
    bodyWeight_kg: args.bodyWeight_kg,
    bw_pc: args.bw_pc,
    equipment: args.equipment,
  });
  if (raisedWeight_kg === undefined) {
    return undefined;
  }

  // both options are judged at the rep target each would PRESCRIBE. The raise returns to the
  // preferred target — judging it at a target that has climbed to 17 would make it look far harder
  // than it is, and the reps would climb past 20 without the weight ever moving. The load itself is
  // still derived for the path's target as before: deriving it for the preferred target instead
  // would raise healthy users further (loop test 1 meso 4: 70 kg x 10 against 9 would go to 75, not
  // 72.5).
  const raisedEstimated1RM = calculate1RM(raisedWeight_kg, args.raisedTargetReps, args.bodyWeight_kg, args.bw_pc).estimated1rm_net_kg;
  const oneMoreRepEstimated1RM = calculate1RM(args.loggedWeight_kg, args.loggedReps + 1, args.bodyWeight_kg, args.bw_pc).estimated1rm_net_kg;
  // a tie goes to the weight, as the ordinary Case-1/Case-2 comparison does
  if (raisedEstimated1RM <= oneMoreRepEstimated1RM) {
    return { move: BeatTargetMove.Weight, newTargetWeight_kg: raisedWeight_kg, newTargetReps: args.raisedTargetReps, anchorRepsToSeed: undefined };
  }

  return {
    move: BeatTargetMove.Reps,
    newTargetWeight_kg: args.loggedWeight_kg,
    // #5979: never past the ceiling. `deriveBeatTargetWeight_kg` refuses sets above 20 reps, so this
    // cannot bind today; it keeps the rule true if that limit ever moves.
    newTargetReps: Math.min(args.loggedReps + 1, MAX_TARGET_REPS),
    // if the user has no preferred target yet, remember the one this set was judged at. Otherwise a
    // later set seeds it from the raised target (11), and the eventual raise would be judged at — and
    // return to — 11 reps instead of 8.
    anchorRepsToSeed: args.anchorReps === undefined ? args.raisedTargetReps : undefined,
  };
}

/**
 * What happens to a user whose rep target would pass `MAX_TARGET_REPS` (#5979). The owner's rule:
 * the weight goes up one step, back at the preferred rep target, silently — unless one step up allows
 * fewer reps than the lowest rep target this session type ever prescribes (`minTargetReps`, from
 * `getMinTargetRepsForIntensity`: 4 for an ordinary session). Then the step is too big for this
 * exercise, and the user is offered a switch to another one (the coaches' rule). `undefined` when
 * the equipment cannot go heavier, because the maxed-out-equipment flow (`decideMaxedOutEquipment`)
 * owns that case.
 *
 * The strength is read as if the set had stopped at `MAX_USABLE_STRENGTH_TEST_REPS` reps. At 30 reps
 * `calculate1RM` is extrapolation (about 2.8x apart between its three formulas), and it would tell a
 * user who did 2.5 kg x 30 that 13 reps at 5 kg are realistic. The user did do those first 20 reps,
 * so the estimate from them is one they proved. See `design-docs/reactive-deload.md`, #4165 part 6.
 */
export function decideRepCeiling(args: {
  /** the weight the ceiling set was lifted with */
  weight_kg: number;
  /** that weight moved one loadable step up */
  nextStep_kg: number;
  /** the user's preferred rep target, which the raise returns to */
  anchorReps: number;
  /** the lowest rep target this session type prescribes: the next weight must allow at least this many */
  minTargetReps: number;
  bodyWeight_kg: number;
  bw_pc: number;
}): RepCeilingProposal | undefined {
  const { weight_kg, nextStep_kg, anchorReps, minTargetReps, bodyWeight_kg, bw_pc } = args;
  // if the equipment cannot make anything heavier, there is no next weight to judge
  if (!Number.isFinite(weight_kg) || !Number.isFinite(nextStep_kg) || !(nextStep_kg > weight_kg)) {
    return undefined;
  }

  const provenEstimated1RM = calculate1RM(weight_kg, MAX_USABLE_STRENGTH_TEST_REPS, bodyWeight_kg, bw_pc).estimated1rm_net_kg;

  // if one step up allows fewer than the minimum reps, the step is too big for this exercise. A set
  // at the next weight passes when its estimated one-rep max stays within the proven one; calculating
  // it forwards keeps the one-rep case and a bodyweight share right, which an inverse would not.
  const minimumSetAtNextStep1RM = calculate1RM(nextStep_kg, minTargetReps, bodyWeight_kg, bw_pc).estimated1rm_net_kg;
  if (minimumSetAtNextStep1RM > provenEstimated1RM) {
    return { action: RepCeilingAction.Switch };
  }
  return { action: RepCeilingAction.Raise, newWeight_kg: nextStep_kg, newTargetReps: Math.min(anchorReps, MAX_TARGET_REPS) };
}

/** Everything `isFollowupAtStrengthTestWeight` needs. */
export interface StrengthTestFollowupInput {
  /** the follow-up set just concluded — its logged weight and reps */
  setStats: Pick<SetStats, 'weight_kg' | 'reps'>;
  /** what the follow-up branch of `concludeSet` scored it, which is still unset when it scored nothing */
  setSuccess: SetSuccess | undefined;
  /** this exercise's sets BEFORE the concluded one, oldest first */
  earlierSets: Pick<Log_WorkoutSet, 'setType' | 'setSuccess' | 'stats'>[];
  /** the path's rep target (`planPath.target_reps`) */
  targetReps: number | undefined;
  /** the weight the follow-up branch judged the set against (`roundedTargetWeight_kg`) */
  storedTargetWeight_kg: number | undefined;
  equipment: Equipment;
}

/**
 * Whether a follow-up set did what a passed strength test asked of it, while the set is still
 * scored nothing (#5620).
 *
 * A strength test that beats its rep target stores a heavier working weight, but the rest of the
 * session keeps offering the tested weight (owner decision, 2026-09-17). The follow-up branch of
 * `concludeSet` judges against the stored weight, so a follow-up that repeats the tested weight at
 * the rep target is scored neither a success nor a partial success and gets no message. This
 * answers "should that set get an informational note instead?".
 *
 * It never makes the set a success: a success feeds `openSet`'s "two sets hit target" rule, which
 * would prefill the raised weight in the same session. See `design-docs/reactive-deload.md` (#5620).
 */
export function isFollowupAtStrengthTestWeight(input: StrengthTestFollowupInput): boolean {
  const { setStats, setSuccess, earlierSets, targetReps, storedTargetWeight_kg, equipment } = input;

  // if the follow-up branch already scored the set, it has its own outcome and needs no note
  if (setSuccess != null) {
    return false;
  }

  // the set this chain of follow-ups started from: the nearest earlier set that is not a follow-up
  const chainStart = [...earlierSets].reverse().find(set => set.setType !== SetType.Followup);
  // if that is not a strength test that passed, this is not the situation the note is about
  if (!chainStart || chainStart.setType !== SetType.NonRMTest || chainStart.setSuccess !== SetSuccess.Ok) {
    return false;
  }

  const testedWeight_kg = chainStart.stats?.weight_kg;
  if (!Number.isFinite(testedWeight_kg) || !Number.isFinite(setStats.weight_kg) || !Number.isFinite(setStats.reps)) {
    return false;
  }
  // if the user went lighter than the weight they tested with, they did not do what was asked
  const snappedTested_kg = adjustWeight(testedWeight_kg, 'round', equipment).weight_kg;
  if (isLoggedWeightBelowRecommended(setStats.weight_kg, snappedTested_kg, equipment)) {
    return false;
  }
  // if they did fewer reps than the rep target, they did not do what was asked either
  if (targetReps == null || setStats.reps < targetReps) {
    return false;
  }
  // only while the set sits below the stored weight — at or above it the existing arms score it
  return storedTargetWeight_kg != null && isLoggedWeightBelowRecommended(setStats.weight_kg, storedTargetWeight_kg, equipment);
}

/**
 * Re-decide the #5620 note on the follow-ups already concluded after the strength test at
 * `testIndex`. Changes those sets' `postStatus` in place.
 *
 * A correction re-runs `concludeSet` for the corrected set only. Without this, a test corrected into
 * a failure, or into a result that no longer raises the stored weight, would leave its follow-ups
 * saying the test already moved the progression. On a first conclusion no later set is done yet, so
 * nothing changes.
 *
 * The chain is walked the way `isFollowupAtStrengthTestWeight` finds its start: every set up to the
 * first one that is not a follow-up. A follow-up that is not concluded (opened and left, or skipped)
 * is stepped over rather than ending the walk, or a concluded one after it would keep a stale note.
 */
export function refreshStrengthTestFollowupNotes(
  sets: Pick<Log_WorkoutSet, 'setType' | 'setSuccess' | 'stats' | 'state' | 'postStatus'>[],
  testIndex: number,
  context: Pick<StrengthTestFollowupInput, 'targetReps' | 'storedTargetWeight_kg' | 'equipment'>,
): void {
  for (let j = testIndex + 1; j < sets.length; j++) {
    const followup = sets[j];
    // the chain ends at the first set that is not a follow-up
    if (followup.setType !== SetType.Followup) {
      return;
    }
    // if this follow-up is not concluded, it carries no note and earns none yet
    if (followup.state !== LogRecordState.Done) {
      continue;
    }
    followup.postStatus = (followup.postStatus ?? []).filter(p => p.status !== PostSetStatus.StrengthTestFollowupDone);
    if (isFollowupAtStrengthTestWeight({ ...context, setStats: followup.stats, setSuccess: followup.setSuccess, earlierSets: sets.slice(0, j) })) {
      followup.postStatus.push({ status: PostSetStatus.StrengthTestFollowupDone, textInserts: undefined });
    }
  }
}

/** What the follow-up branch of `concludeSet` scores an ordinary follow-up set. */
export enum FollowupProgression {
  /** an earlier set this session hit the target and this one did again: `concludeSet` runs
   *  `targetHit` and tells the user the target moved up, when sets remain */
  TargetHitAgain = 'targetHitAgain',
  /** no earlier set hit the target and this one did: `concludeSet` runs `targetHit` */
  TargetHit = 'targetHit',
  /** the set cleared the rep floor at the target weight: stored as `SetSuccess.MinReps`, and the floor rises */
  MinReps = 'minReps',
  /** the set scored nothing */
  None = 'none',
}

/** Check if a set is a non-RM strength test that passed (`concludeSet` stores a pass as `Ok`). */
function isPassedStrengthTest(set: Pick<Log_WorkoutSet, 'setType' | 'setSuccess'>): boolean {
  return set.setType === SetType.NonRMTest && set.setSuccess === SetSuccess.Ok;
}

/** Everything `classifyFollowupProgression` needs. */
export interface FollowupProgressionInput {
  /** the follow-up set just concluded — its logged weight and reps */
  setStats: Pick<SetStats, 'weight_kg' | 'reps'>;
  /** this exercise's sets BEFORE the concluded one, oldest first */
  earlierSets: Pick<Log_WorkoutSet, 'setType' | 'setSuccess' | 'stats'>[];
  /** the weight the set is judged against, snapped onto the equipment (`roundedTargetWeight_kg`) */
  roundedTargetWeight_kg: number | undefined;
  /** the path's rep target (`planPath.target_reps`) */
  targetReps: number;
  /** the user's preferred rep target for this intensity, when they have one (`getPreferRepTarget`) */
  anchorReps: number | undefined;
  /** the path's armed rep floor (`planPath.min_reps`) */
  minReps: number;
}

/**
 * Score an ordinary follow-up set (`SetType.Followup`, not the exercise's first set). The side effects
 * stay in `concludeSet`; this only decides which of them apply. First matching rule wins:
 *
 * 1. an earlier set this session hit the target, and this one matched it (same weight, at least as
 *    many reps) or beat it (heavier, at least the preferred rep target) → `TargetHitAgain`, the
 *    "two sets hit target in a row" autoprogression;
 * 2. no earlier set hit the target, and this one reached the target weight and rep target → `TargetHit`;
 * 3. it reached the target weight and the armed rep floor, is heavier than any set that already
 *    hit the target this session (#6578), and is stronger than a passed strength test this session
 *    (#6929) → `MinReps`;
 * 4. otherwise → `None`.
 */
export function classifyFollowupProgression(input: FollowupProgressionInput): FollowupProgression {
  const { setStats, earlierSets, roundedTargetWeight_kg, targetReps, anchorReps, minReps } = input;
  // the most recent set before this one that did hit the target
  const earlierTargetSet = [...earlierSets].reverse().find(set => set.setSuccess === SetSuccess.TargetReps);
  // how this set's weight compares with that set's. Two weights within the grid tolerance are the same
  // plate weight (#3879), so a hair of conversion noise counts as neither heavier nor different
  const hitWeight_kg = earlierTargetSet?.stats.weight_kg;
  const isSameWeightAsHit = !!earlierTargetSet && Math.abs(setStats.weight_kg - hitWeight_kg) <= WEIGHT_GRID_TOLERANCE_KG;
  const isHeavierThanHit = !!earlierTargetSet && setStats.weight_kg > hitWeight_kg + WEIGHT_GRID_TOLERANCE_KG;

  // if an earlier set hit the target and this one matched or beat it, the target is hit again. compare
  // against that set's own weight, not the stored target: the hit may already have raised the target
  if ((isSameWeightAsHit && setStats.reps >= earlierTargetSet.stats.reps) ||
    (isHeavierThanHit && setStats.reps >= (anchorReps ?? targetReps))) {
    return FollowupProgression.TargetHitAgain;
  }
  // if no earlier set hit the target but this one reached it (unusual), the target is hit
  if (!earlierTargetSet && setStats.weight_kg >= roundedTargetWeight_kg && setStats.reps >= targetReps) {
    return FollowupProgression.TargetHit;
  }
  // #6578: a follow-up no heavier than a set that already hit the target this session is judged only
  // against that set (rule 1). A hit resets the floor to 1, and when it raised only the rep target the
  // target weight did not move, so any weaker set at that weight would otherwise pass here and become
  // next session's benchmark, with its own reps + 1 as the floor.
  const isJudgedByFloor = !earlierTargetSet || isHeavierThanHit;
  // #6929: the same holds after a passed strength test, which is stored as Ok and so is not found as
  // a hit above. When it landed on its target, or ran past it and only the rep target moved (#5927),
  // the floor is 1 at the tested weight. a follow-up no heavier than the test and with no more reps
  // than it is weaker than the test, and scores nothing
  const strengthTest = earlierSets.find(isPassedStrengthTest);
  const isWeakerThanStrengthTest = !!strengthTest &&
    setStats.weight_kg <= strengthTest.stats.weight_kg + WEIGHT_GRID_TOLERANCE_KG && setStats.reps <= strengthTest.stats.reps;
  // if the target is not hit yet but the rep floor is, the floor and the benchmark move up
  if (isJudgedByFloor && !isWeakerThanStrengthTest && setStats.weight_kg >= roundedTargetWeight_kg && setStats.reps >= minReps) {
    return FollowupProgression.MinReps;
  }
  return FollowupProgression.None;
}

/**
 * Check if a concluded set becomes the path's benchmark (`lastStats`), the set next session's first
 * set is compared against. A set that hit the target or cleared the rep floor does. So does a set
 * no weaker than the stored benchmark, for the weird cases where the stats obviously improved but
 * the scoring above did not catch it (this happens for follow-up sets, usually).
 *
 * A passed non-RM strength test does too (#6929). It is stored as `Ok`, never `TargetReps` (#5620),
 * and on a new path there is no benchmark to beat, so without this the first weaker follow-up became
 * the benchmark. An RM test does not: it is a near-max set, and next session's rep test would be
 * judged against it.
 */
export function isNewBenchmarkSet(set: Pick<Log_WorkoutSet, 'setType' | 'setSuccess' | 'stats'>, lastStats: SetStats | undefined): boolean {
  if (set.setSuccess === SetSuccess.TargetReps || set.setSuccess === SetSuccess.MinReps) {
    return true;
  }
  if (isPassedStrengthTest(set)) {
    return true;
  }
  return !!lastStats && set.stats.reps >= lastStats.reps && set.stats.weight_kg >= lastStats.weight_kg;
}

/** Everything `decideTargetHitProgression` needs to decide how a successful set progresses. */
export interface TargetHitProgressionInput {
  /** the reps the user actually performed */
  loggedReps: number;
  /** the rep target the app determined for this path (`planPath.target_reps`) */
  determinedTargetReps: number | undefined;
  /** the user's stored rep-target anchor (`preferRepTargetByIntensity[level]`), or undefined when they have none yet */
  anchorReps: number | undefined;
  /**
   * True for the exercise's FIRST set of the session — the benchmark the progression decision is
   * based on. Only that set may take the #4165 beat-the-target weight jump; a follow-up logging the
   * same performance again must not compound off a target this session already moved.
   */
  isBenchmarkSet: boolean;
  /**
   * The target weight already on the path (`planPath.target_weight_kg`) BEFORE this set concluded,
   * or undefined when the path carries none. A follow-up set is never allowed to write a target
   * weight below this — see the floor at the end of `decideTargetHitProgression` (#5338).
   */
  storedTargetWeight_kg: number | undefined;
  /** the weight the user just lifted */
  currentWeight_kg: number;
  /** that weight moved one loadable step up */
  currentPlusOneStep_kg: number;
  /** the logged set's `stats.estimated1rm_net_kg`, already computed by `estimate1RM` */
  loggedEstimated1rm_net_kg: number;
  bodyWeight_kg: number;
  bw_pc: number;
  equipment: Equipment;
  /**
   * the lowest rep target this path's session type prescribes (`getMinTargetRepsForIntensity`) — the
   * rep ceiling's bar (#5979). Absent means an ordinary session's (4).
   */
  minTargetReps?: number;
}

/** What the caller must persist after a successful set. */
export interface TargetHitProgressionResult {
  /** the anchor to store, or undefined to leave whatever is already stored alone */
  anchorRepsToSeed: number | undefined;
  newTargetWeight_kg: number;
  /** undefined only when there was no rep target to begin with, meaning "leave it as it was" */
  newTargetReps: number | undefined;
  /** true when there was no usable rep target, so the caller can surface it instead of guessing */
  hasNoDeterminedTarget: boolean;
  progression: PostSetStatus.ImmediateProgression | PostSetStatus.ImmediateProgressionReps;
  /**
   * Set only when this benchmark set would have pushed the rep target past `MAX_TARGET_REPS` (#5979).
   * For a raise, the result above already carries it, and the caller does nothing more: the owner
   * ruled that the user is not told (the next session just shows the heavier weight). For a switch, the result holds the rep target at the ceiling, which is what a declined offer leaves,
   * and the caller asks the user whether to switch.
   */
  repCeiling?: RepCeilingProposal;
}

/**
 * Decide the next target weight and rep target after a set hit its target — the pure half of
 * `WorkoutDetailPage.targetHit`. Extracted so it can be unit-tested away from `concludeSet`'s side
 * effects, for the same reason `classifyRegular1stProgression` was (the workout loader normalizes
 * seeded plan state, which makes an end-to-end repro non-deterministic — see #3720).
 *
 * The caller keeps everything impure: pushing the maxed-out-equipment status, writing the anchor
 * onto `fitnessState`, and mirroring the result onto the main path and `planSubslotBackup`.
 */
export function decideTargetHitProgression(input: TargetHitProgressionInput): TargetHitProgressionResult {
  const {
    loggedReps, determinedTargetReps, anchorReps, isBenchmarkSet, storedTargetWeight_kg,
    currentWeight_kg, currentPlusOneStep_kg, loggedEstimated1rm_net_kg,
    bodyWeight_kg, bw_pc, equipment, minTargetReps = getMinTargetRepsForIntensity(IntensityLevel.Initial),
  } = input;

  const hasDeterminedTarget = Number.isFinite(determinedTargetReps);

  // #4165: the logged weight x reps are only a progression STARTING POINT — they must never redefine
  // the REP TARGET. This capped value is what the two places that write a rep target read, so a user
  // who grinds out 20 reps against a target of 8 re-enters the loop at 8 instead of being handed 21.
  //
  // It is deliberately NOT fed into the Case-1/Case-2 comparison below. That comparison weighs a
  // plate against a rep and is tuned around the raw performance; feeding it the capped value shifts
  // the balance towards Case 2 on sets that are not overshoot corrections at all, which showed up in
  // loop test 1 (meso 4) as a follow-up set DROPPING the weight its own first set had just raised.
  const cappedReps = hasDeterminedTarget ? Math.min(loggedReps, determinedTargetReps) : loggedReps;

  // seed the user's anchor from this set when they have none yet. Only the FIRST successful target
  // seeds it; later hits must not overwrite, so the Case-2 +1 bumps oscillate around the original
  // anchor instead of chasing it upward.
  let preferReps = anchorReps;
  let anchorRepsToSeed: number | undefined = undefined;
  if (preferReps === undefined) {
    anchorRepsToSeed = cappedReps;
    preferReps = cappedReps;
  }

  // #4165: beating the rep target moves the WEIGHT, not the target. When the equipment can produce
  // the heavier load, that decides the whole progression and the ordinary comparison below is skipped.
  //
  // Only the exercise's benchmark (first) set may do this. A follow-up set logs the SAME performance
  // again, but by then the first set has already moved the weight and possibly pulled the rep target
  // down to the anchor — so re-deriving would compound off a target this session just changed, and
  // the outcome would depend on how many sets the user logged rather than on what they lifted. Loop
  // test 1 meso 4 catches exactly that: 70 kg x 10 twice gave 72.5 on set 1 and then 75 on set 2.
  //
  // #5927: unless one more rep is the smaller jump — see `decideBeatTargetProgression`.
  if (isBenchmarkSet && hasDeterminedTarget && loggedReps > determinedTargetReps) {
    const beatTarget = decideBeatTargetProgression({
      loggedWeight_kg: currentWeight_kg,
      loggedReps,
      loggedEstimated1rm_net_kg,
      determinedTargetReps,
      raisedTargetReps: Math.min(determinedTargetReps, preferReps),
      // the anchor was seeded just above when missing, so there is nothing for the helper to seed
      anchorReps: preferReps,
      currentPlusOneStep_kg,
      bodyWeight_kg,
      bw_pc,
      equipment,
    });
    if (beatTarget !== undefined) {
      return {
        anchorRepsToSeed,
        newTargetWeight_kg: beatTarget.newTargetWeight_kg,
        newTargetReps: beatTarget.newTargetReps,
        hasNoDeterminedTarget: false,
        progression: beatTarget.move === BeatTargetMove.Weight ? PostSetStatus.ImmediateProgression : PostSetStatus.ImmediateProgressionReps,
      };
    }
  }

  // Case 1: estimate the 1RM for one step more weight, with the rep target adjusted by however far
  // this set ran over the anchor. Reads the RAW logged reps — see the note on `cappedReps` above.
  const repIncrease = loggedReps - preferReps;
  const adjustedTargetRepsForCase1 = preferReps - repIncrease;
  const case1Estimated1RM = calculate1RM(currentPlusOneStep_kg, adjustedTargetRepsForCase1, bodyWeight_kg, bw_pc).estimated1rm_net_kg;

  // Case 2: estimate the 1RM for the same weight with one more rep than was performed.
  const case2Estimated1RM = calculate1RM(currentWeight_kg, loggedReps + 1, bodyWeight_kg, bw_pc).estimated1rm_net_kg;

  // #5927: nor a raise the next session's prescription cap would take back, judging by this set.
  // Once Case-2 bumps have pushed the target two or more above the anchor, the adjusted Case-1 rep
  // count reaches 0 or less and `calculate1RM` scores it 0 — so Case 1 looks like no jump at all and
  // always wins. The cap then shows the old weight at the anchor's reps, and the user climbs the same
  // reps again for ever. Asking the cap itself means the two can never disagree.
  const case1TargetReps = hasDeterminedTarget ? Math.min(determinedTargetReps, preferReps) : undefined;
  const isCase1TakenBackNextSession = hasDeterminedTarget && capPrescribedWeight_kg({
    bestRecentSet: { weight_kg: currentWeight_kg, reps: loggedReps, bw_pc },
    plannedWeight_kg: currentPlusOneStep_kg,
    prescribedReps: case1TargetReps,
    bodyWeight_kg,
    exerciseBw_pc: bw_pc,
    equipment,
  }) !== undefined;

  // df-62615afcc21e: nor, from a set above 20 reps, a raise the rep ceiling would refuse. The
  // cap above judges the set by `calculate1RM`, which past 20 reps is extrapolation: on 2.5 / 5 kg
  // dumbbells it lets 2.5 kg x 26 go to 5 kg x 8, above the 4.5 kg one-rep max the first 20 reps
  // prove. So ask the same question the ceiling asks — does one step up still allow the session's
  // fewest target reps, read at 20 reps? If not, Case 2 holds the weight and the user climbs to the
  // ceiling, where the switch is offered. See `design-docs/reactive-deload.md`, #4165 part 6.
  const isCase1BeyondProvenStrength = loggedReps > MAX_USABLE_STRENGTH_TEST_REPS && decideRepCeiling({
    weight_kg: currentWeight_kg,
    nextStep_kg: currentPlusOneStep_kg,
    anchorReps: preferReps,
    minTargetReps,
    bodyWeight_kg,
    bw_pc,
  })?.action === RepCeilingAction.Switch;

  // take whichever is the smaller jump in estimated 1RM, and never "add weight" the equipment
  // cannot actually add
  const ordinaryResult: TargetHitProgressionResult =
    case1Estimated1RM <= case2Estimated1RM && currentPlusOneStep_kg !== currentWeight_kg
      && !isCase1TakenBackNextSession && !isCase1BeyondProvenStrength
      // Case 1: heavier, and pull the rep target back to the anchor if an earlier Case-2 bump drifted it up
      ? {
        anchorRepsToSeed,
        newTargetWeight_kg: currentPlusOneStep_kg,
        // with no rep target there is nothing to pull back, so hand it back unchanged
        newTargetReps: hasDeterminedTarget ? case1TargetReps : determinedTargetReps,
        hasNoDeterminedTarget: !hasDeterminedTarget,
        progression: PostSetStatus.ImmediateProgression,
      }
      // Case 2: hold the weight and ask for one more rep. Either Case 2 is the smaller 1RM gain, the
      // equipment is at its ceiling, the next session's cap would take Case 1's raise back (#5927),
      // or the raise is beyond what the first 20 reps of the set prove.
      // The +1 comes off the CAPPED reps (#4165), so a set that beat its target asks for one more
      // than the TARGET (8 -> 9) rather than one more than what was performed.
      : {
        anchorRepsToSeed,
        newTargetWeight_kg: currentWeight_kg,
        newTargetReps: cappedReps + 1,
        hasNoDeterminedTarget: !hasDeterminedTarget,
        progression: PostSetStatus.ImmediateProgressionReps,
      };

  // #5338: a follow-up set may RAISE the stored target weight but never lower it. Both cases above
  // derive their weight from the load lifted in THIS set, so a follow-up lifting less than the
  // benchmark just established would write that decision back down. When it would, the benchmark's
  // decision stands whole — weight AND rep target.
  //
  // Reads the RESULT, not the logged weight, so it changes nothing wherever the answer already sits
  // at or above the stored weight. The benchmark set is deliberately not covered — that is #3924's
  // question, decided in `classifyRegular1stProgression`.
  //
  // Why it is needed, and the reported session's numbers: `design-docs/reactive-deload.md`, the
  // #4165 section, part 4.
  if (!isBenchmarkSet && Number.isFinite(storedTargetWeight_kg) && ordinaryResult.newTargetWeight_kg < storedTargetWeight_kg) {
    return {
      anchorRepsToSeed,
      newTargetWeight_kg: storedTargetWeight_kg,
      newTargetReps: hasDeterminedTarget ? determinedTargetReps : undefined,
      hasNoDeterminedTarget: !hasDeterminedTarget,
      // relative to the load just lifted, the next set really is heavier
      progression: PostSetStatus.ImmediateProgression,
    };
  }

  // #5979: the rep target never goes past MAX_TARGET_REPS. When Case 2 would push it past, the
  // benchmark set decides what happens instead: one step heavier at the preferred rep target, or —
  // when that step allows too few reps — the target held at 30 while the user is offered a switch
  // (which is also what a declined offer leaves). A follow-up logs the same performance again, so it
  // only holds the ceiling: the decision belongs to the set the progression is based on.
  if (ordinaryResult.newTargetReps !== undefined && ordinaryResult.newTargetReps > MAX_TARGET_REPS) {
    let repCeiling = isBenchmarkSet
      ? decideRepCeiling({ weight_kg: currentWeight_kg, nextStep_kg: currentPlusOneStep_kg, anchorReps: preferReps, minTargetReps, bodyWeight_kg, bw_pc })
      : undefined;
    // if the next session's prescription cap would take the raise straight back (a high preferred
    // target: 7.5 kg x 28 after 5 kg x 30), the user would climb to 30 and be raised and put back for
    // ever — the loop #5927 removed from Case 1. The step is too big at that target, so offer the
    // switch instead. Asking the cap itself means the two can never disagree.
    const isRaiseTakenBackNextSession = repCeiling?.action === RepCeilingAction.Raise && capPrescribedWeight_kg({
      bestRecentSet: { weight_kg: currentWeight_kg, reps: loggedReps, bw_pc },
      plannedWeight_kg: repCeiling.newWeight_kg,
      prescribedReps: repCeiling.newTargetReps,
      bodyWeight_kg,
      exerciseBw_pc: bw_pc,
      equipment,
    }) !== undefined;
    if (isRaiseTakenBackNextSession) {
      repCeiling = { action: RepCeilingAction.Switch };
    }
    if (repCeiling?.action === RepCeilingAction.Raise) {
      return {
        ...ordinaryResult,
        newTargetWeight_kg: repCeiling.newWeight_kg,
        newTargetReps: repCeiling.newTargetReps,
        progression: PostSetStatus.ImmediateProgression,
        repCeiling,
      };
    }
    return { ...ordinaryResult, newTargetReps: MAX_TARGET_REPS, ...(repCeiling ? { repCeiling } : {}) };
  }

  return ordinaryResult;
}

/**
 * The fewest reps a non-RM strength test may report and still be trusted. A "find the load" test
 * that stopped below this said too little about the weight the user can handle, so the app refuses
 * it and asks for another attempt rather than prescribing from it.
 */
export const MIN_USABLE_NON_RM_TEST_REPS = 3;

/**
 * The most reps an RM (near-maximal) strength test may report and still be trusted. An RM test is
 * MEANT to be a low-rep set, so it is bounded at the top only — there is no lower bound at all, and
 * a single very heavy rep is a perfectly good RM test.
 *
 * This is a different, much tighter limit than `MAX_USABLE_STRENGTH_TEST_REPS`, which bounds the
 * non-RM test. The two are separate numbers because they answer different questions: this one asks
 * "was this actually near-maximal?", the other asks "is the 1RM estimate still an estimate rather
 * than extrapolation?".
 */
export const MAX_USABLE_RM_TEST_REPS = 7;

/**
 * Which rep counts a strength test trusts. `minUsableReps` is left out entirely when the test has
 * no lower bound — see `RM_TEST_REP_BAND`, where that absence also decides which failure an
 * out-of-band rep count reports.
 */
export interface StrengthTestRepBand {
  /** Fewest reps the test trusts, or absent when the test is bounded at the top only. */
  minUsableReps?: number;
  /** Most reps the test trusts. */
  maxUsableReps: number;
}

/** The band a non-RM ("find the working load") strength test trusts: bounded at both ends. */
export const NON_RM_TEST_REP_BAND: StrengthTestRepBand = {
  minUsableReps: MIN_USABLE_NON_RM_TEST_REPS,
  maxUsableReps: MAX_USABLE_STRENGTH_TEST_REPS,
};

/** The band an RM (near-maximal) strength test trusts: bounded at the top only. */
export const RM_TEST_REP_BAND: StrengthTestRepBand = {
  maxUsableReps: MAX_USABLE_RM_TEST_REPS,
};

/** How a completed strength test read against the band its test type trusts. */
export enum StrengthTestOutcome {
  /** The reps landed inside the band, so the test can be used to prescribe from. */
  Passed = 'passed',
  /** The user got more reps than the band allows — the load was too light to learn anything from. */
  FailedTooManyReps = 'failedTooManyReps',
  /** The user got fewer reps than the band allows — only possible for a test with a lower bound. */
  FailedTooFewReps = 'failedTooFewReps',
}

/**
 * Judges a completed strength test's logged reps against the band that test type trusts. The
 * `NonRMTest` and `RMTest` branches of `WorkoutDetailPage.concludeSet` asked this question with
 * near-twin code and now share this one function; each branch still performs its own side effects
 * (which status to record, whether to swap the exercise out, what the sibling paths do next).
 *
 * The order of the checks is load-bearing and is transcribed literally from the branches this
 * replaced: it asks "are these reps USABLE?" first and falls through to a failure. Asking "did it
 * fail?" first and falling through to success reads as the same rule and is not — every comparison
 * against a non-finite number is false, so a set whose `reps` is `undefined` or `NaN` would then
 * classify as a SUCCESSFUL test. `SetStats.reps` is optional and this project does not compile with
 * `strictNullChecks`, so that is reachable rather than theoretical, and on the RM side it would
 * carry a target weight derived from a 1RM that is not a number into the user's plan.
 */
export function classifyStrengthTest(reps: number, band: StrengthTestRepBand): StrengthTestOutcome {
  const withinBand = band.minUsableReps === undefined
    ? reps <= band.maxUsableReps
    : reps >= band.minUsableReps && reps <= band.maxUsableReps;
  if (withinBand) {
    return StrengthTestOutcome.Passed;
  }
  // a band with no lower bound has only one way to fail, and its original was a bare `else` — so a
  // rep count that is not a usable number lands here rather than being compared again
  if (band.minUsableReps === undefined || reps > band.maxUsableReps) {
    return StrengthTestOutcome.FailedTooManyReps;
  }
  return StrengthTestOutcome.FailedTooFewReps;
}

/**
 * At or above this intensity a derived target weight is rounded DOWN onto the equipment rather than
 * to the nearest rung. Was an inline literal in `concludeSet`'s RM-test branch.
 */
export const ROUND_DOWN_FROM_INTENSITY_PC = 85;

/** Everything `deriveRmTestPathTarget` needs to derive one path's target weight from an RM test. */
export interface RmTestPathTargetInput {
  /** the logged set's `stats.estimated1rm_net_kg` — what the RM test says the user's 1RM is */
  estimated1rm_net_kg: number;
  /** this path's share of that 1RM, in percent (`getSlotPathIntensity`) */
  intensity_pc: number;
  /** the user's own body weight */
  bodyWeight_kg: number;
  /** how much of this lift the user's own body carries, in percent; falsy when it carries none */
  bw_pc: number | undefined;
}

/** What `deriveRmTestPathTarget` worked out for one path. */
export interface RmTestPathTargetResult {
  /** the derived load, BEFORE it is snapped onto the equipment's loadable grid */
  target_weight_kg: number;
  /**
   * True when the derived load came out below zero, which means the user's estimated 1RM is smaller
   * than the part of the lift their own body already carries — the exercise is too hard for them.
   *
   * This is reported separately, and read BEFORE the snap to the equipment, because the equipment
   * grid has no negative rungs: snapping first turns "too hard for this user" into an ordinary light
   * prescription and the signal is gone.
   */
  isBelowZero: boolean;
  /** how the derived load should be snapped onto the equipment */
  rounding: 'round_down' | 'round';
}

/**
 * The per-path target-weight arithmetic that runs after a SUCCESSFUL RM test: what should this path
 * prescribe, given what the test says about the user's one-rep max?
 *
 * Pure arithmetic only. The caller snaps the result onto the equipment and writes it into the plan,
 * because both of those need the page's equipment configuration.
 */
export function deriveRmTestPathTarget(input: RmTestPathTargetInput): RmTestPathTargetResult {
  let target_weight_kg = input.estimated1rm_net_kg * (input.intensity_pc / 100);
  if (input.bw_pc) {
    // the target is tracked by ADDED weight, not net weight, so the share the user's own body
    // carries comes off again
    target_weight_kg -= input.bodyWeight_kg * (input.bw_pc / 100);
  }

  return {
    target_weight_kg,
    isBelowZero: target_weight_kg < 0,
    // a heavy path is rounded DOWN rather than to the nearest rung: at this intensity the nearest
    // rung upwards can be more than the user can actually lift for the prescribed reps
    rounding: input.intensity_pc >= ROUND_DOWN_FROM_INTENSITY_PC ? 'round_down' : 'round',
  };
}

/**
 * What the user has typed into one set's inputs, as far as deciding whether that set may be
 * logged is concerned.
 */
export interface SetLoggableInput {
  /** the reps field, which allows a 0 and is empty (null) before the user types anything */
  reps: number | null | undefined;
  /** the weight field; null when the set has no weight yet */
  weight_kg: number | null | undefined;
  /** true once the set is being edited rather than logged for the first time */
  inEditMode: boolean;
}

/**
 * True when this set may be logged — the rule behind whether the log/save button on the workout
 * page is enabled.
 *
 * Two separate requirements, and the reps one is why this function exists:
 *
 * - **The reps must round to at least 1, always.** `estimate1RM` DELETES all three derived
 *   strength figures (`weight_net_kg` and the two 1RM estimates) when the reps round to 0, and it
 *   leaves the set in the Done state. A set saved that way is a finished set carrying no strength
 *   record at all, and readers of those figures do not guard for their absence: the deload
 *   prescription (`deriveDeloadWeight_net_kg` below) multiplies one of them, so an absent figure
 *   would become a NaN weight that travels on into what the app tells the user to lift. The rounding
 *   mirrors `estimate1RM`'s own, so a value it would round down to 0 is refused here rather than
 *   accepted and then stripped.
 * - **The weight must be present, unless the set is being edited.** This is unchanged
 *   long-standing behaviour: an edit may legitimately save a set that has no weight.
 */
export function isSetLoggable(input: SetLoggableInput): boolean {
  // if the reps do not round to at least 1, refuse. Written as a negated `> 0` rather than a
  // `<= 0` so that a missing rep count is refused too: `Math.round(undefined)` is NaN, and every
  // comparison with NaN is false — so `<= 0` would let it back through.
  if (!(Math.round(input.reps) > 0)) {
    return false;
  }
  return input.inEditMode || input.weight_kg != null;
}

/**
 * From this many reps upwards, a deload is worked out from the working weight instead of from the
 * estimated one-rep maximum.
 *
 * Ten is where the two rules very nearly agree, which is what makes it a good place to switch: at
 * 10 reps `calculate1RM` estimates about 1.34x the working weight, so 60% of that is about 0.80x —
 * the same answer the 80%-of-working-weight rule gives. Below 10 the one-rep-maximum rule is the
 * lower of the two and keeps getting lower as the reps fall; above 10 it climbs away, reaching the
 * working weight itself at around 18 reps and exceeding it beyond that, which would be no deload at
 * all. So the switch both caps that climb and joins the two rules without a jump.
 */
const DELOAD_FROM_WORKING_WEIGHT_FROM_REPS = 10;

/** share of the working weight a deload uses when the failed set ran long (see the constant above) */
const DELOAD_SHARE_OF_WORKING_WEIGHT = 0.8;

/** share of the estimated one-rep maximum a deload targets when the failed set was a short one */
const DELOAD_SHARE_OF_ESTIMATED_1RM = 0.6;

/**
 * The strength figures of the set that caused a deload — always the first set of the exercise.
 *
 * Every field is optional because it can genuinely be absent: `estimate1RM` DELETES all three
 * derived strength figures of a set whose reps round to 0, and nothing recomputes them afterwards.
 */
export interface DeloadWeightInput {
  /** the reps performed on the set that caused the deload */
  reps?: number | null;
  /** the working weight of that set, with body weight and assistance already accounted for */
  weight_net_kg?: number | null;
  /** the one-rep maximum estimated from that set, on the same net basis */
  estimated1rm_net_kg?: number | null;
}

/**
 * The net weight a deload set should be prescribed, or `undefined` when it cannot be worked out.
 *
 * Two ways to reach an answer, because one of them breaks down at high rep counts:
 *
 * - **A short failed set** (under `DELOAD_FROM_WORKING_WEIGHT_FROM_REPS` reps) deloads to 60% of
 *   the one-rep maximum estimated from it. That is the intended rule: the total load lands at 60%
 *   of what the user has just shown they can do for one rep.
 * - **A long failed set** deloads to 80% of the working weight instead. Estimating a 1RM from many
 *   reps gives a large number, and 60% of it can exceed the weight the user was actually lifting —
 *   so the first rule would prescribe a deload HEAVIER than the set that failed.
 *
 * **`undefined` means "cannot derive", and the caller must not prescribe anything.** The figure
 * this reads can be missing (see `DeloadWeightInput`), and multiplying a missing one gives NaN.
 * NaN is not caught downstream — `NaN < 0` is false, so a NaN weight passes every ordinary
 * sanity check and is stored as a null, which is a meaningless prescription shown to the user.
 * So the usable-number test belongs here, beside the arithmetic that can produce the bad value,
 * rather than at each call site.
 *
 * **An explicitly null figure is refused too, not only a deleted one** (df-49c62fdb4f40).
 * `null * 0.6` is 0 rather than NaN, so the result test alone would let a null figure through as a
 * 0 kg prescription — which looks like a legitimate bodyweight answer and reports nothing.
 * `estimate1RM` deletes rather than nulls, so today's writer does not produce that shape; a
 * document written by an older version of the app, or one round-tripped through JSON, can.
 */
export function deriveDeloadWeight_net_kg(input: DeloadWeightInput): number | undefined {
  const fromWorkingWeight = input.reps >= DELOAD_FROM_WORKING_WEIGHT_FROM_REPS;
  const source_net_kg = fromWorkingWeight ? input.weight_net_kg : input.estimated1rm_net_kg;

  // if the figure the chosen rule reads is null or missing, refuse before multiplying: `null`
  // multiplies into 0, which would pass the finiteness test below as a real 0 kg answer
  if (source_net_kg == null) {
    return undefined;
  }

  const derived_net_kg = source_net_kg * (fromWorkingWeight ? DELOAD_SHARE_OF_WORKING_WEIGHT : DELOAD_SHARE_OF_ESTIMATED_1RM);

  // the presence check above does not make this one redundant: a figure that is present can
  // still be NaN, not a number at all, or an infinity, and none of those is a usable weight
  return Number.isFinite(derived_net_kg) ? derived_net_kg : undefined;
}

/**
 * #5276, and the push #4939 named but did not build: what one tap of the Log button should do.
 *
 * There are four possible answers and the page used to pick between them with conditions spread
 * over two branches of an eighty-line method. The decision is here, on its own and unit-tested,
 * because `WorkoutDetailPage` cannot be instantiated in a unit test;
 * `past-workout-relog-guard.test.ts` pins the wiring so the two cannot drift apart.
 *
 * The rule about a FINISHED workout is #4939's ownership rule, stated in full in
 * `util/historical-subslot.ts`: the exercise's most recent concluded session owns live
 * progression, until the first set of the NEXT session of that exercise is concluded. So
 * correcting a set in the session you performed most recently still moves your program, and
 * correcting one in an older session does not — that older result has already been superseded,
 * and letting it write would drag the program back to where it was before the newer session.
 * #4939 built and tested that rule and wired it only to telemetry; this is where it starts
 * deciding something.
 *
 * The defect that prompted it: `RestartExercise` used to be chosen without asking whether the
 * workout was finished. Restarting empties the exercise's logged sets, which is right for a
 * session still being performed — sets 2..N of a strength test were prescribed from set 1, so a
 * changed set 1 means they have to be rebuilt — and destroys a record on a session that has
 * already happened, where those sets were PERFORMED and nothing on the page brings them back.
 */
export enum SetTapRoute {
  /** the set is not being edited: log it for the first time, which is unchanged behaviour */
  LogFreshSet = 'log-fresh-set',
  /** empty the exercise's sets and rebuild them from the corrected strength test. Only ever on a
   *  session the user is performing now. */
  RestartExercise = 'restart-exercise',
  /** re-run the full conclusion, so the corrected set moves the user's program exactly as it
   *  would have done had the corrected numbers been logged at the time */
  ReconcludeWithProgression = 'reconclude-with-progression',
  /** rewrite this one set's own record and touch no program state at all */
  CorrectSetOnly = 'correct-set-only',
}

export interface SetTapRouteInput {
  /** whether the tapped set is the exercise's first set */
  isFirstSet: boolean;
  /** whether the set is being edited, as opposed to logged for the first time */
  inEditMode: boolean;
  setType: SetType;
  /**
   * Whether the workout being shown has already been performed.
   *
   * This is `UiExercise.startedInFuture`, whose name says the opposite of what it means:
   * `workout-loader.service.ts` sets it from `mode < WorkoutLoadMode.Current`, and `Past` is
   * `-1`, so it is true for a finished workout and false for every other mode. It is passed in
   * under an honest name rather than renamed at its source, which would touch unrelated code.
   */
  workoutIsFinished: boolean;
  /** whether the session on screen still owns this exercise's progression */
  sessionRecency: SessionRecency;
}

export function decideSetTapRoute(input: SetTapRouteInput): SetTapRoute {
  // a set being logged for the first time is not a correction of anything, so none of the
  // ownership reasoning below applies to it
  if (!input.inEditMode) {
    return SetTapRoute.LogFreshSet;
  }

  // if re-logging the first set of a strength test on a session still being performed, rebuild
  // the exercise around the new test result
  if (!input.workoutIsFinished) {
    if (input.isFirstSet && input.setType === SetType.RMTest) {
      return SetTapRoute.RestartExercise;
    }
    return SetTapRoute.ReconcludeWithProgression;
  }

  // the workout has been performed. Whether this correction may move the program is decided by
  // ownership alone, for every set type — the owner's decision on 2026-09-16, and the way #4939
  // wrote the rule. `Unknown` means nothing is known about who owns the exercise, and writing the
  // program on a guess is the expensive direction, so it takes the same route as `Superseded`.
  return input.sessionRecency === SessionRecency.Latest
    ? SetTapRoute.ReconcludeWithProgression
    : SetTapRoute.CorrectSetOnly;
}

/**
 * Save a set the user edited on a workout from a PREVIOUS day.
 *
 * The plan is deliberately not re-derived on that path — an old session must not move today's
 * targets — so `WorkoutDetailPage.concludeSet` is skipped and this stands in for it. The part of
 * `concludeSet` that must NOT be skipped is the strength recalculation: `stats.weight_kg` and
 * `stats.reps` are what the user just changed, and the three figures derived from them
 * (`weight_net_kg` and the two 1RM estimates) are stored on the same set. Leaving them behind
 * makes the set say two different things at once, and every later reader believes the derived
 * ones (#4556: a set recording 35 lb kept the strength figures of a 65 lb lift, which the app then
 * turned into a permanent working weight of nearly double what the user could lift).
 *
 * `derivedStatsFrom` is the weight and reps the figures currently on the set were calculated from.
 * When the set still records exactly those, the figures are already right and are left alone. Pass
 * it whenever the caller knows it; omitting it always recalculates.
 */
export function concludeEditedSetWithoutPlanUpdate(
  logSet: Log_WorkoutSet,
  bodyWeight_kg: number,
  derivedStatsFrom?: { weight_kg: number; reps: number },
): void {
  logSet.state = LogRecordState.Done;

  // The caller reaches here for ANY change to the set, mini-reps included, so the weight and reps
  // may not have moved at all. Recomputing then would be pure harm: `estimate1RM` works from the
  // user's bodyweight TODAY, while the figures already on an old set came from their bodyweight on
  // the day of that set, which is recorded nowhere else. On an exercise where the user's own
  // bodyweight is part of the load, that silently and irreversibly rewrites the record.
  if (derivedStatsFrom
      && logSet.stats?.weight_kg === derivedStatsFrom.weight_kg
      && logSet.stats?.reps === derivedStatsFrom.reps) {
    return;
  }

  estimate1RM(logSet.stats, bodyWeight_kg);
}

/**
 * Put the progression state a conclusion writes back into the plan when a set progresses back to
 * what that set was PRESCRIBED — so a correction to an already-finished set is judged against the
 * target the user was actually given, not against the target that set's own first conclusion
 * produced. The exact field list is named below.
 *
 * Why this is needed. `WorkoutDetailPage.concludeSet` takes the workout's own copy of the plan entry
 * (`logExercise.planSubslotBackup`) when the first set is concluded, and then, in the SAME call,
 * `targetHit` writes the new target weight, the new target reps and `min_reps = 1` into every
 * container the path lives in — that copy included. The reactive-deload branch writes `target_reps`
 * the same way. So by the time anybody can edit the set, the workout's copy already carries that
 * conclusion's own result — which is why the correction restores from the SET's own record instead.
 * (Until #5049 the correction also cloned that copy over the page's plan entry, and this paragraph
 * explained why rewinding to it did not rewind those three fields. The clone is gone; the reason the
 * copy is the wrong record to judge a correction against is unchanged and is stated above.)
 *
 * `prescribed` is the set's own `Log_WorkoutSet.planPathBackup`, cloned from `planPath` in the call
 * that concluded it, at the point where the path holds what that set was PRESCRIBED. Correcting set
 * three therefore rewinds to just before set three, leaving sets one and two's progression alone;
 * only correcting the first set rewinds to the original prescription.
 *
 * **"What it was prescribed" is not the same as "before anything in `concludeSet` ran", and the
 * clone's position says so (`df-4127ee779713`).** It is taken after the block that runs when the
 * first set is concluded, because one write in that block is part of the prescription rather than a
 * result: after a training break the loader decides a reduced target weight and shows it, but only
 * that block writes the number into the path. Cloning earlier recorded a target the user was never
 * asked to lift. So a NEW progression write added to that block would be captured here and would
 * poison this record — put a genuine progression write after the clone, and see
 * `design-docs/volume-autoregulation.md` before moving either.
 *
 * if the set carries no such record — a session logged before the field existed, or a set concluded
 * with no path — there is nothing to restore and this does nothing, which leaves the correction
 * behaving exactly as it did before this rule existed.
 *
 * What is restored: every path field a conclusion writes back — `target_weight_kg`, `target_reps`,
 * `min_reps`, `lastDeload1rm_net_kg`, `lastStats`, `reachedMaxWeightConfiguration`,
 * `orgTarget_weight_kg`, `nextWorkoutSetType` and `nextWorkoutNonRMTestRequired`.
 *
 * The `autoregulation*` control state is the one thing left out, and on purpose: it does not
 * describe what the plan prescribed — it says a question is open, and that question survives the
 * correction.
 *
 * **Why the list has to be complete, which is not obvious.** Before #4890 most of these were
 * written through the active path alone, so on set one's first conclusion they reached the plan
 * sub-slot only and the caller's own clone of the workout's copy threw them away again — restoring
 * them by accident. #4890 sent those writes to both containers, which removed the accident, so
 * anything left out here now SURVIVES the correction. (Not every conclusion write was converted —
 * the ones still reaching a single container are recorded in `design-docs/deferred-findings/`.
 * `nextWorkoutNonRMTestRequired` used to be one of them; since `df-ad72c95954fc` the passing
 * strength-test branch clears it in every container, so restoring it here is now the whole
 * story for that field.) That is invisible while the
 * re-run takes the same branch as the first conclusion and writes the field again; it bites when
 * the correction changes WHICH branch runs. Worked example: a `NonRMTest` set first concluded
 * inside the trusted rep band takes the passed branch, which writes `orgTarget_weight_kg` and calls
 * `establishWorkingPath`; correct that set to fewer reps and the re-run takes a failure branch that
 * writes neither, so a retracted successful test would leave its working weight and set type
 * standing. **So a new progression write belongs in this list unless the re-run is guaranteed to
 * rewrite it on every path it can take.**
 *
 * `lastDeload1rm_net_kg`, `lastStats` and `reachedMaxWeightConfiguration` joined the list with
 * #4890, and the reason is worth knowing. Before #4890 a conclusion wrote them through the active
 * path alone, so on set one's first conclusion they reached the plan sub-slot and the rewind's own
 * clone of the workout's copy threw them away again — restoring them by accident. #4890 made every
 * conclusion write reach both containers — bar the exceptions noted above — which removed the
 * accident. Without them here, a
 * correction would be judged against state its own first conclusion created: a deload yardstick
 * seconds old would read as a plateau, and on a first-ever session — where `lastStats` is the only
 * baseline there is — a corrected set would be measured against the lift the user had just taken
 * back, offering a deload nobody earned. That is the very defect this rewind exists to prevent.
 *
 * `planSubSlot.plateauWorkoutLog_id` is not restored here, and it is not left alone either: it lives
 * on the sub-slot rather than the path, so this function cannot reach it, and the caller rewinds it
 * separately with `withdrawPlateauRaisedBySet` — immediately after calling this, and before it
 * re-runs the conclusion. See
 * `design-docs/volume-autoregulation.md`, "Correcting an already-finished set".
 *
 * Pass every container the path lives in (`UiExercise.livePathsForCurrentIntensity()`), not just the
 * active path: writing through one leaves the others stale, which is right in memory and wrong after
 * a reload.
 *
 * This reaches the PERFORMED intensity only. The retest demand a conclusion wrote onto the other
 * intensities is taken back by `restoreOtherIntensityRetestDemands` (df-72de01d6fc68).
 *
 * The owner's rules for this, and why the already-logged later sets are deliberately NOT replayed,
 * are in `design-docs/volume-autoregulation.md`.
 */
export function restorePrescribedTargets(livePaths: Plan_ExercisePath[], prescribed: Plan_ExercisePath | undefined): void {
  if (!prescribed) {
    return;
  }

  for (const path of livePaths) {
    path.target_weight_kg = prescribed.target_weight_kg;
    path.target_reps = prescribed.target_reps;
    path.min_reps = prescribed.min_reps;
    path.lastDeload1rm_net_kg = prescribed.lastDeload1rm_net_kg;
    // each container gets its own clone — sharing one object between them would let a later edit
    // of one silently change the other
    path.lastStats = prescribed.lastStats ? deepClone(prescribed.lastStats) : undefined;
    path.reachedMaxWeightConfiguration = prescribed.reachedMaxWeightConfiguration;
    path.orgTarget_weight_kg = prescribed.orgTarget_weight_kg;
    path.nextWorkoutSetType = prescribed.nextWorkoutSetType;
    path.nextWorkoutNonRMTestRequired = prescribed.nextWorkoutNonRMTestRequired;
  }
}

/**
 * df-72de01d6fc68: record the strength-retest demand every OTHER intensity holds, just before a set
 * is concluded — the companion record to `planPathBackup`, which covers the performed intensity only.
 *
 * Why these two fields and nothing else. A conclusion writes the retest demand
 * (`nextWorkoutSetType` and `nextWorkoutNonRMTestRequired`) onto EVERY intensity, and a re-run after
 * a correction is not guaranteed to write it again — the rep-test branch never does, because a
 * correction asks nobody the weight question. The other all-intensity writes (the strength test's
 * target weights) are rewritten by every re-run that reaches them, so they need no record.
 *
 * Returns `undefined` when there is no other intensity, so a single-intensity exercise stores nothing.
 */
export function recordOtherIntensityRetestDemands(
  pagePaths: Plan_ExercisePath[],
  performedIntensity: IntensityLevel,
): Log_RetestDemandBackup[] | undefined {
  const backup: Log_RetestDemandBackup[] = [];
  for (const path of pagePaths) {
    if (path.intensityLevel === performedIntensity) {
      continue;
    }
    const entry: Log_RetestDemandBackup = {
      intensityLevel: path.intensityLevel,
      nextWorkoutSetType: path.nextWorkoutSetType,
    };
    if (path.nextWorkoutNonRMTestRequired !== undefined) {
      entry.nextWorkoutNonRMTestRequired = path.nextWorkoutNonRMTestRequired;
    }
    backup.push(entry);
  }
  return backup.length > 0 ? backup : undefined;
}

/**
 * df-72de01d6fc68: the correction rewind for the other intensities — put back the strength-retest
 * demand each of them held before the corrected set was first concluded, so a demand that set wrote
 * does not outlive the correction. The re-run that follows writes it again wherever the corrected
 * numbers still earn it.
 *
 * `pathsForIntensity` answers which live path objects a write to that intensity must reach
 * (`UiExercise.pageAndPlanPathsForIntensity`); whether that includes the live program is decided
 * there, by the same ownership rule every other write to a non-performed intensity follows. An
 * intensity the exercise no longer has resolves to no paths and is skipped. A set with no record
 * restores nothing, which is what the app did before the record existed.
 *
 * df-3397e1d4c18b: the page never calls this for a temporary exercise swap. The swap's conclusion
 * writes no plan path, and a record it carries was read off the REPLACED exercise's paths, so after
 * the slot is switched permanently to the swapped-in exercise it would land on the new exercise.
 * See `design-docs/volume-autoregulation.md`.
 */
export function restoreOtherIntensityRetestDemands(
  pathsForIntensity: (intensity: IntensityLevel) => Plan_ExercisePath[],
  backup: Log_RetestDemandBackup[] | undefined,
): void {
  if (!backup) {
    return;
  }

  for (const entry of backup) {
    for (const path of pathsForIntensity(entry.intensityLevel)) {
      path.nextWorkoutSetType = entry.nextWorkoutSetType;
      if (entry.nextWorkoutNonRMTestRequired === undefined) {
        delete path.nextWorkoutNonRMTestRequired;
      } else {
        path.nextWorkoutNonRMTestRequired = entry.nextWorkoutNonRMTestRequired;
      }
    }
  }
}

/**
 * How far past the anchor a typed set must reach before the app asks "Is this correct?".
 *
 * Was an inline `2.0` in `onClickSetDone`. #5174 tightened it to 1.5, which is safe ONLY because
 * `strengthGuardAnchor_net_kg` below now judges against something that tracks the user session by
 * session. Against the lifetime best it replaced, 1.5 would have questioned an honest trainee: a
 * user who climbs from 35 lb x 16 to 70 lb x 8 over a few months is at 1.58x their old best, and
 * that is a good session, not a typo.
 *
 * What 1.5 costs against a FRESH anchor, for a sense of scale: prescribed 100 lb x 8 is only
 * questioned above 150 lb, or above 22 reps. See `design-docs/reactive-deload.md`.
 */
export const STRENGTH_GUARD_FACTOR = 1.5;

/**
 * The best one-rep max among the sets already CONCLUDED in this session for one exercise, or
 * `undefined` when none has been concluded yet.
 *
 * A set with no usable estimate is skipped rather than counted as zero, so a half-filled set cannot
 * drag the answer down. Used by both halves of #5174: it is what gets stored
 * (`nextStoredStrength_net_kg`) and one of the things a typed set is judged against
 * (`strengthGuardAnchor_net_kg`).
 */
/* Sibling worth knowing about: `bestStrengthSetOfSession` (#5176) answers a similar-sounding
 * question for the prescription cap, and the two are deliberately separate. That one returns the raw
 * set rather than a number, recomputes the strength figure with today's bodyweight instead of
 * reading the stored one, and skips deload and dropset sets. This one is right for what it feeds —
 * the stored per-exercise estimate — and must keep counting every completed set. */
export function bestStrengthOfSession_net_kg(sets: Log_WorkoutSet[]): number | undefined {
  let best: number | undefined;
  for (const set of sets) {
    // only sets the user has actually finished say anything about their strength
    if (!set || set.state !== LogRecordState.Done) {
      continue;
    }
    const estimate_net_kg = set.stats?.estimated1rm_net_kg;
    if (!isFinite(estimate_net_kg) || estimate_net_kg <= 0) {
      continue;
    }
    if (best === undefined || estimate_net_kg > best) {
      best = estimate_net_kg;
    }
  }
  return best;
}

/**
 * What `ExerciseStat.estimated1RM_net_kg` should hold after this conclusion: the best of the sets
 * concluded in this session, or the value already stored when this session has concluded none.
 *
 * This REPLACED a rule that kept whichever was larger of the new value and the stored one (#5174).
 * That made the stored number a personal record which could never come down: a customer had
 * 47.045 kg stored for Overhead Triceps Extension, computed from a set he mistyped as 65 lb and
 * then corrected to 35 lb, and nothing he logged afterwards could lower it. His real level is about
 * 25.33 kg, so the guard that reads this number sat at a threshold of 94 kg and never fired again
 * on that exercise.
 *
 * Keeping the stored value when the session has concluded nothing matters: the alternative would
 * write `undefined` over a perfectly good number the first time this ran with an empty set list.
 */
export function nextStoredStrength_net_kg(sets: Log_WorkoutSet[], stored_net_kg?: number): number | undefined {
  const sessionBest_net_kg = bestStrengthOfSession_net_kg(sets);
  return sessionBest_net_kg !== undefined ? sessionBest_net_kg : stored_net_kg;
}

/**
 * Every set concluded for one exercise anywhere in this workout, newest container last.
 *
 * Not the same as one screen row's sets: a workout can hold the same exercise in two sub-slots (the
 * duplicate-subslot shape in `design-docs/volume-reoptimize.md`), and both share ONE `ExerciseStat`,
 * because that is keyed by exercise id. Reading a single container would let the second occurrence
 * overwrite what the first stored, discarding a heavier set the user really did in the same session.
 */
export function sessionSetsForExercise(workoutExercises: Log_WorkoutExercise[], exerciseId: string): Log_WorkoutSet[] {
  const sets: Log_WorkoutSet[] = [];
  for (const workoutExercise of workoutExercises || []) {
    if (workoutExercise?.exerciseId !== exerciseId) {
      continue;
    }
    for (const set of workoutExercise.sets || []) {
      sets.push(set);
    }
  }
  return sets;
}

/** What `prescribedImplied1RM_net_kg` needs to say how strong the app expected this set to be. */
export interface PrescribedStrengthInput {
  /** the weight the app asked for, ALREADY snapped onto the equipment's loadable grid */
  prescribedWeight_kg?: number;
  /** how many reps it asked for at that weight */
  targetReps?: number;
  /** the user's own body weight */
  bodyWeight_kg: number;
  /** how much of this lift the user's own body carries, in percent; falsy when it carries none */
  bw_pc?: number;
}

/**
 * The one-rep max implied by the weight the app itself prescribed — the strength guard's one
 * candidate that a corrupt logged set cannot poison, and the only one that already carries a
 * training-break reduction.
 *
 * Returns `undefined` when there is nothing to work from, which is a real case rather than an error:
 * a temp swap prescribes nothing, and the loader deliberately leaves a test set's path without a
 * target. The caller then judges the set against its other candidates.
 *
 * A prescribed weight of zero is a legitimate answer, not a missing one — a bodyweight exercise with
 * no added load prescribes exactly that, and the user's own body still supplies the effort — so only
 * the REPS are required to be above zero.
 */
export function prescribedImplied1RM_net_kg(input: PrescribedStrengthInput): number | undefined {
  if (!isFinite(input.prescribedWeight_kg) || !isFinite(input.targetReps) || input.targetReps <= 0) {
    return undefined;
  }
  const { estimated1rm_net_kg } = calculate1RM(input.prescribedWeight_kg, input.targetReps, input.bodyWeight_kg, input.bw_pc);
  // a lift the user's own body already outweighs can produce a non-positive figure; that is not a
  // strength the guard can measure against, so report it as nothing to judge by
  return isFinite(estimated1rm_net_kg) && estimated1rm_net_kg > 0 ? estimated1rm_net_kg : undefined;
}

/** Everything `strengthGuardAnchor_net_kg` can judge a typed set against. */
export interface StrengthGuardAnchorInput {
  /** the one-rep max implied by the weight the app PRESCRIBED, at that path's target reps */
  prescribedImplied_net_kg?: number;
  /** the best of this session's already-concluded sets (`bestStrengthOfSession_net_kg`) */
  sessionBest_net_kg?: number;
  /** the previous session's strongest SUCCESSFUL set for this exercise (`UiExercise.previousFirstSetStats`),
   *  which is bounded to that one session and is not a lifetime figure */
  previousSessionBest_net_kg?: number;
  /** the value stored on `ExerciseStat`, used only when nothing fresher exists */
  stored_net_kg?: number;
}

/**
 * What a typed set is judged against by the "Is this correct?" guard: the largest of the three
 * FRESH candidates, falling back to the stored value only when none of them exists.
 *
 * The fallback is deliberately a fallback and NOT a fourth candidate in the maximum (#5174). An
 * inflated stored value would win every maximum it entered, which is exactly the state that made
 * the guard useless for the customer above: his threshold sat at 94 kg on an exercise where he
 * lifts 25 kg, so he could type more than twice his real strength and be asked nothing.
 *
 * Why the three are ranked as equals rather than in preference order: each covers a case the others
 * miss. The prescribed weight is the one candidate a corrupt logged set cannot poison, and it
 * already carries any training-break reduction. This session's earlier sets stop the app asking
 * twice about a heavy load the user has already confirmed once. The previous session protects a
 * user coming back from a break, whose reduced prescription sits well below what they can still
 * lift - and it is bounded to one session, so it cannot become a lifetime mark of its own.
 * Taking the largest means a candidate can only ever make the guard quieter, so no single one of
 * them can produce a false alarm on its own.
 *
 * Deliberately NOT a candidate: `Plan_ExercisePath.lastStats`. It is only written when a set
 * succeeds or a strength test passes, so it is a near-high-water mark itself — the same trap in a
 * different field.
 */
export function strengthGuardAnchor_net_kg(input: StrengthGuardAnchorInput): number | undefined {
  let anchor: number | undefined;
  for (const candidate of [input.prescribedImplied_net_kg, input.sessionBest_net_kg, input.previousSessionBest_net_kg]) {
    if (!isFinite(candidate) || candidate <= 0) {
      continue;
    }
    if (anchor === undefined || candidate > anchor) {
      anchor = candidate;
    }
  }
  if (anchor !== undefined) {
    return anchor;
  }
  // nothing fresh to judge against - the stored value is better than no guard at all
  return isFinite(input.stored_net_kg) && input.stored_net_kg > 0 ? input.stored_net_kg : undefined;
}

/**
 * Is this typed set so far above the anchor that the app should ask the user whether they meant it?
 *
 * Answers false when there is no usable anchor. That is not a new decision — it is what the code
 * this replaced already did by accident, since `x > 2.0 * undefined` is `NaN > ...`, which is false.
 * Stating it here makes a first-ever set on an exercise an explicit "no guard" rather than a
 * property of how JavaScript compares against `NaN`.
 */
export function isSetImplausiblyStrong(
  implied_net_kg: number,
  anchor_net_kg: number | undefined,
  factor: number = STRENGTH_GUARD_FACTOR,
): boolean {
  if (anchor_net_kg === undefined || !isFinite(implied_net_kg)) {
    return false;
  }
  return implied_net_kg > factor * anchor_net_kg;
}

/** What `targetHit` decides about maxed-out equipment for one set that hit its rep target. */
export interface MaxedOutEquipmentDecision {
  // the weight to store as "maxed out at", or undefined when the equipment can still go heavier
  maxedOutAt_kg: number | undefined;
  // true when a machine is assigned but broken, and it would have been called maxed out — worth reporting
  skippedBrokenEquipment: boolean;
}

/**
 * Decide whether a set that hit its rep target has maxed out its equipment: the step up from the
 * target weight gives back the target weight itself, so nothing heavier can be loaded.
 *
 * On equipment the app cannot read, every weight calculation returns its input unchanged, so that
 * test is true for every weight and means nothing. Recording it swapped a user's exercise away on
 * the next load (#4337, #5860), so it is never recorded then. Two kinds of unreadable equipment:
 * - none assigned at all (missing, or the `Unknown` type): a normal state, nothing to report.
 * - a machine is assigned but fails `isEquipmentValid`: a broken setup, so the caller reports it.
 */
export function decideMaxedOutEquipment(
  target_kg: number | undefined,
  targetPlusOneStep_kg: number | undefined,
  equipment: Equipment | undefined,
): MaxedOutEquipmentDecision {
  const noHeavierStep = target_kg !== undefined && targetPlusOneStep_kg === target_kg;
  if (!noHeavierStep) {
    return { maxedOutAt_kg: undefined, skippedBrokenEquipment: false };
  }
  if (isEquipmentValid(equipment)) {
    return { maxedOutAt_kg: target_kg, skippedBrokenEquipment: false };
  }
  const hasAssignedEquipment = !!equipment && equipment.type !== EquipmentType.Unknown;
  return { maxedOutAt_kg: undefined, skippedBrokenEquipment: hasAssignedEquipment };
}

/**
 * df-ecb462a04f94 (#6618): keep a "maxed out at" weight only if the target the set leaves behind is
 * still at that weight. `decideMaxedOutEquipment` judges the target stored BEFORE the set, and a
 * strength test can leave an old, heavier target there: 410 lb (the top of the plate set) while the
 * test lifted 250 lb and the new target became 255 lb. Kept, that marker swapped the exercise away
 * at the next load although the plates had 155 lb of room above the new target.
 *
 * `newTarget_kg` is the new target already rounded onto the equipment.
 */
export function maxedOutAtAfterProgression_kg(maxedOutAt_kg: number | undefined, newTarget_kg: number): number | undefined {
  if (maxedOutAt_kg === undefined) {
    return undefined;
  }
  return newTarget_kg < maxedOutAt_kg - WEIGHT_GRID_TOLERANCE_KG ? undefined : maxedOutAt_kg;
}

/** What the workout loader does with a stored "maxed out at" weight. */
export enum MaxedOutMarkerAction {
  // the target is still at the marker and nothing heavier can be loaded: suggest another exercise
  SwitchExercise = 'SwitchExercise',
  // the target is at the marker but the equipment now goes heavier: step the target up, drop the marker
  StepUp = 'StepUp',
  // the target is below the marker, so the marker is out of date: drop it and leave the target alone
  DropStale = 'DropStale',
}

/**
 * Decide what to do at load with a path's `reachedMaxWeightConfiguration`. The marker means "this
 * target was hit at the heaviest the equipment could load". When the target has since moved below
 * it (a strength test, a deload), it no longer says anything about the current target, and stepping
 * that target up would always land at or under the marker and swap the exercise away
 * (df-ecb462a04f94, #6618). Such a marker is dropped without stepping up: the user has not hit the
 * current target yet. Both weights are already rounded onto the equipment.
 */
export function decideMaxedOutMarkerAtLoad(marker_kg: number, target_kg: number, targetPlusOneStep_kg: number): MaxedOutMarkerAction {
  if (target_kg < marker_kg - WEIGHT_GRID_TOLERANCE_KG) {
    return MaxedOutMarkerAction.DropStale;
  }
  return targetPlusOneStep_kg <= marker_kg ? MaxedOutMarkerAction.SwitchExercise : MaxedOutMarkerAction.StepUp;
}
